import React, { useState, useEffect } from 'react';
import { useSidePanel } from '../../context/SidePanelContext';
import { usePrices } from '../../context/PriceContext';
import { useToday } from '../../context/TodayContext';
import { getEiaCountdown, getBreadth, getNiftyBreadthHistory, getNiftyHistory, getFoStocks, getNews, getExtremes, getGiftNifty, saveEiaForecast, getIndexQuote, getStrategy4Events } from '../../api/client';
import { getFilteredNews, getTabReports, generateTabReport } from '../../api/ai';
import { OVERNIGHT as MOCK_OVERNIGHT } from '../../data/mock';
import { INDEX_REGISTRY } from '../../data/indices';

// ── Data hook — fetches API data and reshapes to exact mock.js shapes ─────────
// All UI components below this hook are 100% original — zero changes.

// Stable skeleton arrays defined outside the hook (never recreated)
// INDICES_SKELETON order: clockwise from top-left → NIFTY, BANKNIFTY, VIX, SENSEX
const INDICES_SKELETON = [
  {id:'NIFTY50',   name:'NIFTY 50',   price:null, chg:null, pct:null, spark:Array(10).fill(0)},
  {id:'BANKNIFTY', name:'Bank Nifty', price:null, chg:null, pct:null, spark:Array(10).fill(0)},
  {id:'VIX',       name:'India VIX',  price:null, chg:null, pct:null, spark:Array(10).fill(0)},
  {id:'SENSEX',    name:'Sensex',     price:null, chg:null, pct:null, spark:Array(10).fill(0)},
];

// Global markets — prices fetched via getIndexQuote (yfinance EOD), not Kite live
// Order: left→right by timezone: Japan, HK (Asia) | DAX, Dow (Europe+US) | S&P, Nasdaq (US)
const GLOBAL_MARKETS_SKELETON = [
  {id:'nikkei',   name:'Nikkei 225',  price:null, chg:null, pct:null, unit:'JPY', spark:Array(10).fill(0)},
  {id:'hangseng', name:'Hang Seng',   price:null, chg:null, pct:null, unit:'HKD', spark:Array(10).fill(0)},
  {id:'dax',      name:'DAX',         price:null, chg:null, pct:null, unit:'EUR', spark:Array(10).fill(0)},
  {id:'dow',      name:'Dow Jones',   price:null, chg:null, pct:null, unit:'USD', spark:Array(10).fill(0)},
  {id:'sp500',    name:'S&P 500',     price:null, chg:null, pct:null, unit:'USD', spark:Array(10).fill(0)},
  {id:'nasdaq',   name:'Nasdaq',      price:null, chg:null, pct:null, unit:'USD', spark:Array(10).fill(0)},
];

// Commodities — MCX pairs and ETFs removed; pure US/international prices only
const COMMODITIES_SKELETON = [
  {id:'ng_us',     name:'Nat Gas (US)',    price:null, chg:null, pct:null, unit:'$/MMBtu', spark:Array(10).fill(0)},
  {id:'crude_us',  name:'US Oil (WTI)',    price:null, chg:null, pct:null, unit:'$/bbl',   spark:Array(10).fill(0)},
  {id:'gold_us',   name:'Gold (XAUUSD)',   price:null, chg:null, pct:null, unit:'$/oz',    spark:Array(10).fill(0)},
  {id:'silver_us', name:'Silver (XAGUSD)', price:null, chg:null, pct:null, unit:'$/oz',    spark:Array(10).fill(0)},
  {id:'usdinr',    name:'USD/INR',         price:null, chg:null, pct:null, unit:'₹',       spark:Array(10).fill(0)},
  {id:'btc',       name:'Bitcoin (BTC)',   price:null, chg:null, pct:null, unit:'USD',     spark:Array(10).fill(0)},
];

function useAdaptedData() {
  const { indicesRaw, commodsRaw, kiteStatus } = usePrices();

  const spk = (base) => Array.from({length:10},(_,i)=>+(base+Math.sin(i*.9)*base*.04).toFixed(2));

  const [INDICES,        setINDICES]        = useState(INDICES_SKELETON);
  const [GLOBAL_MARKETS, setGLOBAL_MARKETS] = useState(GLOBAL_MARKETS_SKELETON);
  const [SGX, setSGX] = useState({id:'SGX',name:'GIFT Nifty',price:null,chg:null,pct:null,spark:Array(10).fill(0),kiteConnected:false});
  const [COMMODITIES,    setCOMMODITIES]    = useState(COMMODITIES_SKELETON);
  const [EIA,            setEIA]            = useState({nextDate:'—',nextTime:'—',lastActual:0,lastForecast:0,lastSurprise:0,tag:'Bearish',countdown:'—',history:[]});
  const [BREADTH,        setBREADTH]        = useState({nifty50:{today:{adv:0,dec:0},hist:[]},fo:{today:{adv:0,dec:0},hist:[]},total:{today:{adv:0,dec:0},hist:[]}});
  const [SECTORS,        setSECTORS]        = useState([]);
  const [BROAD,          setBROAD]          = useState([]);
  const [THEMATICS,      setTHEMATICS]      = useState([]);
  const [SPARKS,         setSPARKS]         = useState({});
  const [FO_MOVERS,      setFO_MOVERS]      = useState({gainers:[],losers:[],volume:[]});
  const [EXTREMES,       setEXTREMES]       = useState({highs:[], lows:[]});
  const [fiiHistory,     setFiiHistory]     = useState([]);

  const OVERNIGHT = MOCK_OVERNIGHT;

  // ── React to indicesRaw (from PriceContext — refreshed every 2 min) ─────
  useEffect(() => {
    if (!indicesRaw) return;
    const idx = indicesRaw.indices || [];
    const find = (label) => idx.find(i => i.label === label);

    // Indices cards — order: NIFTY50, BANKNIFTY, VIX, SENSEX (clockwise from top-left)
    const idxMap = [
      ['NIFTY50',  'NIFTY50',   'NIFTY 50'],
      ['BANKNIFTY','BANKNIFTY',  'Bank Nifty'],
      ['INDIAVIX', 'VIX',        'India VIX'],
      ['SENSEX',   'SENSEX',     'Sensex'],
    ];
    const nseHoliday = indicesRaw.is_trading_day === false; // backend says not a trading day
    setINDICES(idxMap.map(([label, id, name]) => {
      const item = find(label);
      if (!item || item.ltp == null) return {id, name, price:null, chg:null, pct:null, spark:Array(10).fill(0), nseHoliday};
      const price = item.ltp;
      const prev  = item.prev_close != null ? item.prev_close : price;
      return { id, name, price, chg: +(price-prev).toFixed(2), pct: item.chg_pct!=null?item.chg_pct:0, spark: spk(price), nseHoliday };
    }));

    // Sector heatmap
    // Derived from INDEX_REGISTRY — edit indices.js to change names, not here
    const sectorDefs = INDEX_REGISTRY
      .filter(e => e.category === 'Sector' && e.kiteShortLabel)
      .map(e => [e.kiteShortLabel, e.sectorName || e.label]);
    setSECTORS(sectorDefs.map(([lbl,name])=>{
      const item=find(lbl); if(!item||item.ltp==null) return null;
      return {name, pct:item.chg_pct!=null?item.chg_pct:0};
    }).filter(Boolean));

    // Broad market
    // Derived from INDEX_REGISTRY — edit indices.js to change names, not here
    const broadDefs = INDEX_REGISTRY
      .filter(e => (e.category === 'Broad' || e.category === 'Index') && e.kiteShortLabel && e.id !== 'indiavix')
      .map(e => [e.kiteShortLabel, e.label]);
    setBROAD(broadDefs.map(([lbl,name])=>{
      const item=find(lbl); if(!item||item.ltp==null) return null;
      return {name, pct:item.chg_pct!=null?item.chg_pct:0};
    }).filter(Boolean));

    // Thematic indices (CPSE, PSE, Commodities, MNC, Services)
    // Derived from INDEX_REGISTRY — edit indices.js to change names, not here
    const thematicDefs = INDEX_REGISTRY
      .filter(e => e.category === 'Thematic' && e.kiteShortLabel)
      .map(e => [e.kiteShortLabel, e.sectorName || e.label]);
    setTHEMATICS(thematicDefs.map(([lbl,name])=>{
      const item=find(lbl); if(!item||item.ltp==null) return null;
      return {name, pct:item.chg_pct!=null?item.chg_pct:0};
    }).filter(Boolean));
  }, [indicesRaw]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── GIFT Nifty — reacts to both indicesRaw and kiteStatus ───────────────
  useEffect(() => {
    if (!indicesRaw) return;
    let cancelled = false;
    const idx = indicesRaw.indices || [];
    const find = (label) => idx.find(i => i.label === label);
    const n50  = find('NIFTY50');
    const niftyProxy = n50 && n50.ltp ? {
      id:'SGX', name:'NIFTY 50 (prev close)',
      price: n50.ltp, chg: +(n50.ltp - (n50.prev_close || n50.ltp)).toFixed(2),
      pct: n50.chg_pct || 0, spark: spk(n50.ltp), kiteConnected: false,
    } : null;

    if (kiteStatus?.connected) {
      getGiftNifty().then(gift => {
        if (cancelled) return;
        if (gift && gift.ltp) {
          setSGX({ id:'SGX', name:'GIFT Nifty', price:gift.ltp, chg:gift.chg||0,
                   pct:gift.chg_pct||0, prevClose:gift.prev_close||null,
                   spark:spk(gift.ltp), kiteConnected:true,
                   niftyFutLtp:   gift.nifty_fut_ltp   ?? null,
                   niftyFutLabel: gift.nifty_fut_label ?? null,
                 });
        } else if (niftyProxy) {
          setSGX(niftyProxy);
        }
      }).catch(() => { if (!cancelled && niftyProxy) setSGX(niftyProxy); });
    } else if (niftyProxy) {
      setSGX(niftyProxy);
    }

    return () => { cancelled = true; };
  }, [indicesRaw, kiteStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── React to commodsRaw (from PriceContext — same 2-min cadence) ─────────
  useEffect(() => {
    if (!commodsRaw) return;
    const items = commodsRaw.commodities || [];
    const findC = (id) => items.find(i => i.id === id);
    setCOMMODITIES(COMMODITIES_SKELETON.map(skeleton => {
      const item = findC(skeleton.id);
      if (!item || item.ltp == null) return skeleton;
      const price = item.ltp;
      const prev  = item.prev_close != null ? item.prev_close : price;
      return {...skeleton, price, chg: +(price-prev).toFixed(price<10?4:2),
               pct: item.chg_pct!=null?item.chg_pct:0, spark: spk(price)};
    }));
  }, [commodsRaw]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Secondary data — EIA, breadth, fo-stocks, extremes, news ───────────
  // These are not in PriceContext so we fetch them directly here.
  useEffect(() => {
    let mounted = true;
    async function fetchSecondary() {
      await Promise.allSettled([
        // EIA
        getEiaCountdown().then(res => {
          const weeks = res.last_10_weeks || res.last_5_weeks || [];
          const history = weeks.map(w => {
            const actual   = w.change_bcf != null ? w.change_bcf : 0;
            const forecast = w.forecast_bcf != null ? w.forecast_bcf : null;
            const surprise = w.surprise_bcf != null ? w.surprise_bcf : null;
            const indication = w.indication || null;
            const bull     = actual < 0;
            const d        = w.period || '';
            const parts    = d.split('-');
            const months   = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            const week     = parts.length === 3 ? `w/e ${months[+parts[1]-1]} ${parts[2]}` : d;
            return { week, actual, forecast, surprise, indication, bull, period: w.period };
          });
          const lastRow = history[0] || {};
          const tag = lastRow.indication
            ? lastRow.indication
            : (lastRow.actual || 0) < 0 ? 'Bullish' : 'Bearish';
          const hrs     = res.countdown_hours || 0;
          const days    = Math.floor(hrs / 24);
          const hrsRem  = Math.floor(hrs % 24);
          const minsRem = Math.round((hrs % 1) * 60);
          if (mounted) setEIA({
            nextDate: res.next_report || '—', nextTime: res.next_report_ist || '—',
            lastActual: lastRow.actual || 0, lastForecast: lastRow.forecast,
            lastSurprise: lastRow.surprise, tag,
            countdown: `${days}d ${hrsRem}h ${minsRem}m away`, history,
          });
        }).catch(e => console.error('EIA:', e)),

        // Breadth — nifty_daily is the single source of truth for bars.
        // Live bar only appended during actual NSE market hours (weekday 9:15–15:30 IST).
        Promise.all([getBreadth(), getNiftyBreadthHistory(10)]).then(([res, histRes]) => {
          const n50 = res.nifty50     || {advances:0, declines:0};
          const fo  = res.fo_universe || {advances:0, declines:0};

          // is_trading_day: backend confirms today is a real NSE trading day
          const isTradingDay = histRes.is_trading_day !== false;
          const marketOpen   = isTradingDay && isMarketOpen('NSE');
          const todayStr     = new Date(new Date().toLocaleString('en-US', {timeZone:'Asia/Kolkata'}))
                                 .toISOString().slice(0, 10);

          // All DB rows (actual trading days from nifty_daily), oldest → newest
          const dbRows = (histRes.rows || []).slice().reverse();

          const makeBar = r => {
            const adv = r.adv || 0;
            const dec = r.dec || r.dec_count || Math.max(0, 50 - adv);
            const t   = adv + dec || 1;
            return { a: Math.round(adv/t*100), d: Math.round(dec/t*100), date: r.date, adv, dec };
          };

          let hist, todayAdv, todayDec;

          if (marketOpen) {
            // Market hours: 9 DB bars (prior days) + 1 live bar
            const pastRows = dbRows.filter(r => r.date !== todayStr).slice(-9);
            const padCount = Math.max(0, 9 - pastRows.length);
            const padding  = Array(padCount).fill(null).map(() => ({a:0,d:0,adv:0,dec:0,date:null}));
            const pastBars = pastRows.map(makeBar);
            const tt = n50.advances + n50.declines || 1;
            const liveBar = {
              a: Math.round(n50.advances/tt*100), d: Math.round(n50.declines/tt*100),
              date: todayStr, adv: n50.advances, dec: n50.declines,
              live: true,  // drives "now" label and LIVE tooltip
            };
            hist     = [...padding, ...pastBars, liveBar];
            todayAdv = n50.advances;
            todayDec = n50.declines;
          } else {
            // Market closed / weekend / holiday: only nifty_daily rows, no forced today bar
            const pastRows = dbRows.slice(-10);
            const padCount = Math.max(0, 10 - pastRows.length);
            const padding  = Array(padCount).fill(null).map(() => ({a:0,d:0,adv:0,dec:0,date:null}));
            hist = [...padding, ...pastRows.map(makeBar)];
            // Counts from latest nifty_daily (last real trading day)
            const latest = pastRows[pastRows.length - 1];
            todayAdv = latest?.adv || 0;
            todayDec = latest?.dec || latest?.dec_count || 0;
          }

          if (mounted) setBREADTH({
            nifty50: {today:{adv:todayAdv,               dec:todayDec},               hist},
            fo:      {today:{adv:fo.advances,             dec:fo.declines},            hist},
            total:   {today:{adv:todayAdv + fo.advances,  dec:todayDec + fo.declines}, hist},
          });
        }).catch(e => console.error('Breadth:', e)),

        // F&O Stocks
        getFoStocks().then(res => {
          const stocks = (res.stocks||[]).filter(s=>s.chg_pct!=null&&!s.error&&s.ltp);
          const byGain = [...stocks].sort((a,b)=>(b.chg_pct||0)-(a.chg_pct||0));
          const gainers = byGain.slice(0,10).map(s=>({
            name: s.display_symbol||s.symbol?.replace('.NS',''), pct: +(s.chg_pct||0).toFixed(2),
          }));
          const losers = [...byGain].reverse().slice(0,10).map(s=>({
            name: s.display_symbol||s.symbol?.replace('.NS',''), pct: +(s.chg_pct||0).toFixed(2),
          }));
          const byVol = [...stocks].sort((a,b)=>(b.volume||0)-(a.volume||0)).slice(0,10).map(s=>{
            const v=s.volume||0;
            return { name: s.display_symbol||s.symbol?.replace('.NS',''),
                     vol: v>=1e6?`${(v/1e6).toFixed(1)}M`:v>=1e3?`${(v/1e3).toFixed(0)}K`:'0' };
          });
          if (mounted) setFO_MOVERS({gainers, losers, volume:byVol});
        }).catch(e => console.error('FO Stocks:', e)),

        // 52W Extremes
        getExtremes().then(res => {
          if (res && (res.highs || res.lows) && mounted)
            setEXTREMES({ highs: res.highs||[], lows: res.lows||[] });
        }).catch(e => console.error('Extremes:', e)),

        // News
        getNews().then(res => {
          const ICON = {'Indian Equity':'📈','Natural Gas & Energy':'⛽','Macro':'🏦','Global Markets':'🌐','FII Flows':'💸','Geopolitical':'🌍','NSE/BSE Circulars':'📋'};
          const CKEY = {'Indian Equity':'ie','Natural Gas & Energy':'ng','Macro':'mac','Global Markets':'gl','FII Flows':'fii','Geopolitical':'geo','NSE/BSE Circulars':'circ'};
          const rel  = (iso)=>{ if(!iso) return ''; const m=(Date.now()-new Date(iso).getTime())/60000; return m<60?`${Math.floor(m)}m`:m<1440?`${Math.floor(m/60)}h`:`${Math.floor(m/1440)}d`; };
          // No .slice cap — keep full 7-day pool so impact filters can show last-20 from full history
          _NEWS_DATA = (res.items||[]).map((n,i)=>({
            id:n.id||String(i), cat:n.category, catKey:CKEY[n.category]||'other',
            icon:ICON[n.category]||'📰', src:n.source, time:rel(n.published), hl:n.title, url:n.url||'',
          }));
        }).catch(e => console.error('News:', e)),

        // Classified news — fetch ALL impacts (HIGH/MEDIUM/IGNORE) for full 7-day window
        // Keyed by item_id (exact MD5 match with n.id from getNews) — no fragile title matching
        getFilteredNews('HIGH,MEDIUM,IGNORE', 168, 500).then(res => {
          const map = {};
          (res.items||[]).forEach(item => {
            if (item.item_id) map[item.item_id] = { impact: item.impact, rationale: item.rationale||'' };
          });
          _CLASSIFIED_MAP = map;
        }).catch(() => {}), // silent — classification is best-effort

        // Dashboard AI digest — new format has sentiment+news_digest+overnight
        getTabReports('dashboard', 1).then(res => {
          const latest = res.reports?.[0];
          if (latest?.report_json) {
            try {
              const parsed = JSON.parse(latest.report_json);
              // New 3-section format: store the whole report object
              if (parsed.sentiment || parsed.news_digest?.length) {
                _OVERNIGHT_DATA = parsed; // full report object
              } else if (parsed.overnight?.length) {
                // Legacy format (array only)
                _OVERNIGHT_DATA = parsed.overnight;
              }
            } catch {}
          }
        }).catch(() => {}),
      ]);
    }
    fetchSecondary();
    const iv = setInterval(fetchSecondary, 300_000);
    return () => { mounted=false; clearInterval(iv); };
  }, []);

  // ── FII Futures history — last 20 trading days ───────────────────────────
  useEffect(() => {
    let mounted = true;
    getNiftyHistory(20).then(res => {
      if (!mounted) return;
      const rows = (res.rows || [])
        .filter(r => r.fiiNetFut != null)
        .slice(-15); // keep last 15 days with data
      if (rows.length) setFiiHistory(rows);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // ── Real sparklines + global market prices via getIndexQuote ────────────
  // Backend resolves YF: prefix via _EXTRA_YF_MAP. Global indices have no Kite
  // live feed, so their price/chg/pct also comes from this response (EOD data).
  useEffect(() => {
    const ALL_SPARKS = [
      // NSE indices (Kite live prices come via indicesRaw; these just fetch sparklines)
      ['NIFTY50',   'NIFTY 50'],
      ['BANKNIFTY', 'NIFTY BANK'],
      ['SENSEX',    'SENSEX'],
      ['VIX',       'INDIA VIX'],
      // Global indices — YF: prefix → backend strips and calls yfinance directly
      ['nikkei',    'YF:^N225'],
      ['hangseng',  'YF:^HSI'],
      ['dax',       'YF:^GDAXI'],
      ['dow',       'YF:^DJI'],
      ['sp500',     'YF:^GSPC'],
      ['nasdaq',    'YF:^IXIC'],
      // Commodities / FX / Crypto
      ['ng_us',     'YF:NG=F'],
      ['crude_us',  'YF:CL=F'],
      ['gold_us',   'YF:GC=F'],
      ['silver_us', 'YF:SI=F'],
      ['usdinr',    'YF:INR=X'],
      ['btc',       'YF:BTC-USD'],
    ];

    const GLOBAL_IDS = new Set(['nikkei','hangseng','dax','dow','sp500','nasdaq']);

    Promise.allSettled(ALL_SPARKS.map(([id, sym]) =>
      getIndexQuote(sym).then(r => ({ id, closes: r?.sparkline_closes ?? [], ltp: r?.ltp, chg_pct: r?.change_pct ?? r?.chg_pct ?? 0, chg: r?.change ?? r?.chg ?? 0 }))
    )).then(results => {
      const sparkMap = {};
      const globalPriceUpdates = [];

      results.forEach(r => {
        if (r.status !== 'fulfilled' || !r.value) return;
        const { id, closes, ltp, chg_pct, chg } = r.value;
        if (Array.isArray(closes) && closes.length >= 2) sparkMap[id] = closes;
        // Capture price data for global indices (their only price source)
        if (GLOBAL_IDS.has(id) && ltp != null) {
          globalPriceUpdates.push({ id, ltp, chg_pct: chg_pct ?? 0, chg: chg ?? 0 });
        }
      });

      // Update global markets with both sparklines and prices
      if (globalPriceUpdates.length || Object.keys(sparkMap).some(k => GLOBAL_IDS.has(k))) {
        setGLOBAL_MARKETS(prev => prev.map(item => {
          const priceData = globalPriceUpdates.find(p => p.id === item.id);
          const spark     = sparkMap[item.id];
          return {
            ...item,
            ...(priceData ? { price: priceData.ltp, chg: priceData.chg, pct: priceData.chg_pct } : {}),
            ...(spark     ? { spark }                                                              : {}),
          };
        }));
      }

      if (Object.keys(sparkMap).length) setSPARKS(sparkMap);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    INDICES:       INDICES.map(i    => SPARKS[i.id] ? {...i, spark: SPARKS[i.id]} : i),
    GLOBAL_MARKETS, // prices + sparklines already merged in the sparklines effect
    SGX,
    COMMODITIES:   COMMODITIES.map(c => SPARKS[c.id] ? {...c, spark: SPARKS[c.id]} : c),
    EIA, BREADTH, SECTORS, BROAD, THEMATICS, FO_MOVERS, EXTREMES, OVERNIGHT,
    fiiHistory,
  };
}

// Module-level vars — set by the hook, read by original components unchanged
let _INDICES=[], _SGX={id:'SGX',name:'SGX Nifty',price:0,chg:0,pct:0,spark:[]};
let _GLOBAL_MARKETS=[];
let _COMMODITIES=[], _EIA={nextDate:'—',nextTime:'—',lastActual:0,lastForecast:0,lastSurprise:0,tag:'Bearish',countdown:'—',history:[]};
let _BREADTH={nifty50:{today:{adv:0,dec:0},hist:Array(10).fill({a:50,d:50})},fo:{today:{adv:0,dec:0},hist:Array(10).fill({a:50,d:50})},total:{today:{adv:0,dec:0},hist:Array(10).fill({a:50,d:50})}};
let _SECTORS=[], _BROAD=[], _THEMATICS=[], _FO_MOVERS={gainers:[],losers:[],volume:[]};
let _NEWS_DATA=[];
// Map of news title → { impact: 'HIGH'|'MEDIUM'|'IGNORE', rationale: string }
// Populated by getFilteredNews() every 5 min alongside raw news
let _CLASSIFIED_MAP={};
// Overnight digest — populated by live dashboard AI report; falls back to mock
let _OVERNIGHT_DATA=[];

// Aliases so original component code reads these names without change
let INDICES=_INDICES, SGX=_SGX, GLOBAL_MARKETS=_GLOBAL_MARKETS, COMMODITIES=_COMMODITIES, EIA=_EIA;
let BREADTH=_BREADTH, SECTORS=_SECTORS, BROAD=_BROAD, THEMATICS=_THEMATICS, FO_MOVERS=_FO_MOVERS;
let NEWS=_NEWS_DATA;
let EXTREMES={highs:[], lows:[]};
let FII_HISTORY=[];


/* ── rgba gradient colour ── */
function heatStyle(pct) {
  const abs = Math.abs(pct);
  const op = Math.min(0.55, abs / 3 * 0.55);
  if (pct >= 0.05) return { background:`rgba(26,106,26,${op.toFixed(2)})`, color: op>0.35?'#e8f5e8':'#1a4a1a' };
  if (pct <= -0.05) return { background:`rgba(136,30,16,${op.toFixed(2)})`, color: op>0.35?'#ffe8e8':'#4a0a00' };
  return { background:'var(--surface-2)', color:'var(--ink-3)' };
}

/* ── Sparkline ── */
function Spark({ data=[], w=60, h=24 }) {
  if (!data||data.length<2) return <div style={{width:w,height:h}}/>;
  const mn=Math.min(...data),mx=Math.max(...data),rng=mx-mn||1;
  const pts=data.map((v,i)=>`${(i/(data.length-1))*(w-4)+2},${h-2-((v-mn)/rng)*(h-4)}`).join(' ');
  const up=data[data.length-1]>=data[0];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{display:'block',flexShrink:0}}>
      <polyline points={pts} fill="none" stroke={up?'var(--up-field-ink)':'var(--down-field-ink)'} strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  );
}

/* ── Market hours helper — computed from IST time, no API call ── */
function isMarketOpen(marketType) {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const h = now.getHours(), m = now.getMinutes(), day = now.getDay();
  const mins = h * 60 + m;
  const isWeekday = day >= 1 && day <= 5;

  if (marketType === 'NSE') {
    // 9:15 AM – 3:30 PM IST, Mon–Fri
    return isWeekday && mins >= 555 && mins < 930;
  }
  if (marketType === 'GIFT') {
    // 6:30 AM–3:40 PM and 4:35 PM–2:45 AM IST (gap 3:40–4:35 only)
    const gapStart = 940, gapEnd = 995;
    if (day === 6) return false; // Saturday fully closed
    return !(mins >= gapStart && mins < gapEnd);
  }
  if (marketType === 'US_FUTURES') {
    // CME: nearly 24hrs, closed Fri 11:30 PM – Sun 11:30 PM IST (approx)
    const friClose = 1410, sunOpen = 1410;
    if (day === 6) return false;
    if (day === 5 && mins >= friClose) return false;
    if (day === 0 && mins < sunOpen) return false;
    return true;
  }
  // Global equity exchanges — approximate IST open/close windows
  if (marketType === 'JAPAN') {
    // TSE: 9:00–15:30 JST → 5:30–12:00 UTC → 11:00–17:30 IST
    return isWeekday && mins >= 660 && mins < 1050;
  }
  if (marketType === 'HK') {
    // HKEX: 9:30–16:00 HKT → 1:30–8:00 UTC → 7:00–13:30 IST (morning session only for simplicity)
    return isWeekday && mins >= 420 && mins < 810;
  }
  if (marketType === 'EUROPE') {
    // XETRA/DAX: 9:00–17:30 CET → 8:00–16:30 UTC → 13:30–22:00 IST
    return isWeekday && mins >= 810 && mins < 1320;
  }
  if (marketType === 'US_EQUITY') {
    // NYSE/NASDAQ: 9:30–16:00 EDT (UTC-4 summer) → 13:30–20:00 UTC → 19:00–01:30 IST
    // Approximate: open 19:00 IST (Mon–Fri), closes 01:30 IST next day
    if (day === 6) return false; // Saturday
    if (day === 0 && mins < 90) return true;  // Sunday early AM still Fri session
    return isWeekday && (mins >= 1140 || mins < 90); // 19:00–01:30 IST
  }
  if (marketType === 'CRYPTO') return true; // always open
  return false;
}

// Map card id → market type
const CARD_MARKET = {
  'NIFTY50':'NSE','BANKNIFTY':'NSE','SENSEX':'NSE','VIX':'NSE',
  'nikkei':'JAPAN','hangseng':'HK','dax':'EUROPE',
  'dow':'US_EQUITY','sp500':'US_EQUITY','nasdaq':'US_EQUITY',
  'ng_us':'US_FUTURES','crude_us':'US_FUTURES',
  'gold_us':'US_FUTURES','silver_us':'US_FUTURES',
  'usdinr':'US_FUTURES','btc':'CRYPTO',
};

/* ── Uniform card — heatmap background based on chg% ── */
function ICard({ item, onNameClick }) {
  const isLoading = item.price == null;
  const pos = (item.pct||0) >= 0;
  const hs = isLoading ? { background:'var(--surface)', color:'var(--ink)' } : heatStyle(item.pct||0);
  const isColoured = !isLoading && Math.abs(item.pct||0) >= 0.05;
  const nameCol  = isColoured ? hs.color : 'var(--ink-2)';
  const priceCol = isColoured ? hs.color : 'var(--ink)';
  const pctCol   = isColoured ? hs.color : (pos ? 'var(--up-field-ink)' : 'var(--down-field-ink)');
  const unitCol  = isColoured ? `${hs.color}99` : 'var(--ink-3)';

  // Market status dot: blue=open+data, amber=open+waiting, grey=closed
  const mktType  = CARD_MARKET[item.id];
  // On a public holiday, NSE is closed regardless of the time window.
  const open     = mktType ? (mktType === 'NSE' && item.nseHoliday ? false : isMarketOpen(mktType)) : null;
  const dotState = open === null ? null
    : !open           ? 'closed'
    : isLoading       ? 'waiting'
    :                   'live';
  const dotCol   = dotState === 'live'    ? '#4a90d9'   // blue
                 : dotState === 'waiting' ? '#c8922a'   // muted amber
                 : dotState === 'closed'  ? '#999999'   // grey
                 : null;
  const dotTitle = dotState === 'live'    ? 'Market open · data live'
                 : dotState === 'waiting' ? 'Market open · waiting for data'
                 : 'Market closed';

  // Absolute change formatting
  const chgAbs = item.chg != null
    ? `${pos?'+':''}${item.chg.toFixed(item.price != null && item.price < 10 ? 4 : item.price != null && item.price < 1000 ? 2 : 2)}`
    : null;

  return (
    <div style={{
      ...hs,
      padding:'8px 10px', display:'flex', flexDirection:'column',
      justifyContent:'space-between', borderRadius:10, minHeight:72,
      border: isLoading ? '.5px solid var(--hair)' : 'none',
    }}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:4}}>
        <div style={{display:'flex',alignItems:'center',gap:4,flex:1,minWidth:0}}>
          <div
            onClick={onNameClick}
            style={{fontSize:11,color:nameCol,fontWeight:500,lineHeight:1.2,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',cursor:onNameClick?'pointer':'default'}}
          >{item.name}</div>
          {dotCol && (
            <span style={{width:7,height:7,borderRadius:'50%',background:dotCol,flexShrink:0}} title={dotTitle}/>
          )}
        </div>
        <Spark data={item.spark} w={44} h={18}/>
      </div>
      {isLoading ? (
        <div style={{fontSize:12,color:'var(--ink-3)',fontFamily:'var(--font-data)',marginTop:4}}>—</div>
      ) : (
        <>
          <div style={{fontSize:14,fontWeight:700,fontFamily:'var(--font-data)',color:priceCol,lineHeight:1.2,marginTop:4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
            {typeof item.price==='number'
              ? item.price.toLocaleString('en-IN',{minimumFractionDigits:item.price<100?3:item.price<1000?2:0})
              : item.price}
          </div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:2}}>
            <div style={{fontSize:11,fontFamily:'var(--font-data)',color:pctCol,whiteSpace:'nowrap'}}>
              {chgAbs && <span>{chgAbs} </span>}
              <span style={{opacity: chgAbs ? 0.8 : 1}}>({pos?'+':''}{item.pct?.toFixed(2)}%)</span>
            </div>
            {item.unit&&<div style={{fontSize:11,color:unitCol}}>{item.unit}</div>}
          </div>
        </>
      )}
    </div>
  );
}

// Maps display names → routing symbol for SidePanel detail + history endpoints.
// NSE stocks/indices: plain symbol. MCX futures: MCX: prefix. yfinance: YF: prefix.
// Auto-derived from INDEX_REGISTRY — covers all labels, sectorNames, and kiteShortLabels.
// Only non-registry entries (commodities, FX, crypto) are hardcoded below.
const _REG_DISPLAY_TO_KITE = {};
INDEX_REGISTRY.forEach(e => {
  _REG_DISPLAY_TO_KITE[e.label]      = e.kiteSymbol;
  if (e.sectorName) _REG_DISPLAY_TO_KITE[e.sectorName] = e.kiteSymbol;
  // Also map kiteShortLabel → kiteSymbol so broadDefs/sectorDefs clicks resolve
  if (e.kiteShortLabel) _REG_DISPLAY_TO_KITE[e.kiteShortLabel] = e.kiteSymbol;
});

const DISPLAY_TO_KITE_SYMBOL = {
  ..._REG_DISPLAY_TO_KITE,
  // Global indices — routed via yfinance
  'Nikkei 225':      'YF:^N225',
  'Hang Seng':       'YF:^HSI',
  'DAX':             'YF:^GDAXI',
  'Dow Jones':       'YF:^DJI',
  'S&P 500':         'YF:^GSPC',
  'Nasdaq':          'YF:^IXIC',
  // Commodities, FX, crypto
  'Nat Gas (US)':    'YF:NG=F',
  'US Oil (WTI)':    'YF:CL=F',
  'Gold (XAUUSD)':   'YF:GC=F',
  'Silver (XAGUSD)': 'YF:SI=F',
  'USD/INR':         'YF:INR=X',
  'Bitcoin (BTC)':   'YF:BTC-USD',
};

/* ── NSE market hours dot for section titles ── */
function NseDot() {
  const nseHoliday = INDICES[0]?.nseHoliday ?? false;
  const open = !nseHoliday && isMarketOpen('NSE');
  const col  = open ? '#4a90d9' : '#999999';
  const tip  = open ? 'NSE market open' : 'NSE market closed';
  return (
    <span style={{width:7,height:7,borderRadius:'50%',background:col,
      display:'inline-block',marginLeft:5,verticalAlign:'middle'}}
      title={tip}/>
  );
}

/* ── Grouped instrument section ── */
function InstrumentGroup({ title, ids, items, cols = 2 }) {
  const { open: openPanel } = useSidePanel();
  const cards = ids.map(id => items.find(i => i.id === id)).filter(Boolean);
  return (
    <div style={{
      minWidth:0,
      background:'var(--surface-2)',
      border:'.5px solid var(--hair-strong)',
      borderRadius:8,
      padding:'8px 10px 10px',
    }}>
      <div style={{fontSize:11,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',
        color:'var(--ink-3)',marginBottom:7,paddingBottom:5,
        borderBottom:'.5px solid var(--hair)'}}>{title}</div>
      <div style={{display:'grid',gridTemplateColumns:`repeat(${cols},1fr)`,gap:5}}>
        {cards.map(item => {
          const kiteSymbol = DISPLAY_TO_KITE_SYMBOL[item.name] || item.name;
          const handleClick = () => openPanel({ name: kiteSymbol });
          return <ICard key={item.id} item={item} onNameClick={handleClick}/>;
        })}
      </div>
    </div>
  );
}

/* ── FII Index Futures Position Row ── */
function FIIFuturesRow() {
  const rows = FII_HISTORY;
  if (!rows.length) return null;

  // Last 10 bars for sparkline (some rows may have null fiiNetFut — already filtered upstream)
  const bars     = rows.slice(-10);
  const latest   = bars[bars.length - 1];
  const todayVal = latest?.fiiNetFut ?? 0;

  // 5-day cumulative net position
  const bias5d   = bars.slice(-5).reduce((s, r) => s + (r.fiiNetFut || 0), 0);

  // Trend badge
  const THRESHOLD = 1500; // ₹1500 Cr threshold for Long/Short label
  const trendLabel = bias5d >  THRESHOLD ? 'Net Long'
                   : bias5d < -THRESHOLD ? 'Net Short'
                   : 'Neutral';
  const trendColor = bias5d >  THRESHOLD ? 'var(--up-field-ink)'
                   : bias5d < -THRESHOLD ? 'var(--down-field-ink)'
                   : 'var(--ink-3)';
  const trendBg   = bias5d >  THRESHOLD ? 'rgba(26,106,26,.15)'
                  : bias5d < -THRESHOLD ? 'rgba(136,30,16,.15)'
                  : 'rgba(120,120,120,.12)';

  // Format ₹ Cr helper
  const fmtCr = v => {
    if (v == null) return '—';
    const abs = Math.abs(v);
    const s   = v >= 0 ? '+' : '−';
    if (abs >= 10000) return `${s}₹${(abs/1000).toFixed(1)}K Cr`;
    return `${s}₹${Math.round(abs).toLocaleString('en-IN')} Cr`;
  };

  // Bar chart dimensions
  const BAR_W = 18, BAR_GAP = 4, CHART_H = 36;
  const maxAbs = Math.max(...bars.map(r => Math.abs(r.fiiNetFut || 0)), 1);
  const chartW = bars.length * (BAR_W + BAR_GAP) - BAR_GAP;

  return (
    <div className="fii-container" style={{
      display:'flex', alignItems:'center', gap:16, flex:1, minWidth:0,
      padding:'8px 14px',
      background:'var(--surface-2)', border:'.5px solid var(--hair-strong)',
      borderRadius:8,
    }}>
      {/* Label */}
      <div style={{minWidth:130}}>
        <div style={{fontSize:11,fontWeight:700,letterSpacing:'.06em',textTransform:'uppercase',
          color:'var(--ink-3)',marginBottom:3}}>FII Index Futures</div>
        <div style={{fontSize:11,color:'var(--ink-3)'}}>
          {bars.length} day rolling · ₹ Crores
        </div>
      </div>

      {/* Bar sparkline — desktop only, hidden on mobile to prevent overflow */}
      <svg className="desktop-only" width={chartW} height={CHART_H} style={{flexShrink:0,overflow:'visible'}}>
        {bars.map((r, i) => {
          const v    = r.fiiNetFut || 0;
          const pct  = Math.abs(v) / maxAbs;
          const bH   = Math.max(2, Math.round(pct * (CHART_H / 2 - 2)));
          const x    = i * (BAR_W + BAR_GAP);
          const mid  = CHART_H / 2;
          const isPos = v >= 0;
          const isToday = i === bars.length - 1;
          return (
            <g key={i}>
              <rect
                x={x} y={isPos ? mid - bH : mid}
                width={BAR_W} height={bH}
                fill={isPos ? 'var(--up-field-ink)' : 'var(--down-field-ink)'}
                opacity={isToday ? 1 : 0.55}
                rx={2}
              />
            </g>
          );
        })}
        {/* Zero line */}
        <line x1={0} x2={chartW} y1={CHART_H/2} y2={CHART_H/2}
          stroke="var(--hair-strong)" strokeWidth={0.8}/>
      </svg>

      {/* Latest recorded day value */}
      <div className="fii-val" style={{minWidth:100}}>
        <div style={{fontSize:11,color:'var(--ink-3)',marginBottom:2}}>
          {latest?.date
            ? new Date(latest.date + 'T00:00:00').toLocaleDateString('en-IN', {day:'numeric',month:'short'})
            : 'Latest'}
        </div>
        <div style={{
          fontFamily:'var(--font-data)', fontSize:13, fontWeight:700, lineHeight:1,
          color: todayVal >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
        }}>
          {fmtCr(todayVal)}
        </div>
      </div>

      {/* 5-day bias */}
      <div className="fii-val" style={{minWidth:100}}>
        <div style={{fontSize:11,color:'var(--ink-3)',marginBottom:2}}>5-Day Cumulative</div>
        <div style={{
          fontFamily:'var(--font-data)', fontSize:13, fontWeight:700, lineHeight:1,
          color: bias5d >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
        }}>
          {fmtCr(bias5d)}
        </div>
      </div>

      {/* Trend badge */}
      <div style={{marginLeft:'auto'}}>
        <span style={{
          fontSize:11, fontWeight:700, padding:'3px 10px',
          borderRadius:4, background:trendBg, color:trendColor,
          border:`.5px solid ${trendColor}`, opacity:.9,
        }}>
          {trendLabel}
        </span>
        <div style={{fontSize:11,color:'var(--ink-3)',textAlign:'center',marginTop:3}}>
          5-day bias
        </div>
      </div>
    </div>
  );
}

/* ── SGX / GIFT Nifty Pre-Market Signal ── */
function SGXCard() {
  const niftyClose = INDICES.find(i=>i.id==="NIFTY50")?.price||0;
  if(!SGX.price) return null;
  // Only show outside NSE market hours (15:30 → 09:15), and on holidays
  const _nseHoliday = INDICES.find(i=>i.id==="NIFTY50")?.nseHoliday ?? false;
  if(!_nseHoliday && isMarketOpen('NSE')) return null;

  // Determine pre vs post market for label/badge
  const _now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const _mins = _now.getHours() * 60 + _now.getMinutes();
  const isPreOpen = _mins < 555; // before 09:15

  // Gap: GIFT Nifty (futures) vs near-month NIFTY futures (apples-to-apples).
  // If the near-month future price isn't available yet (Kite not connected / symbol mismatch),
  // fall back to NIFTY 50 cash as a rough reference.
  const futRef    = SGX.niftyFutLtp;
  const reference = futRef || niftyClose || SGX.prevClose || 0;
  const refLabel  = futRef ? (SGX.niftyFutLabel || 'NIFTY Fut') : 'NIFTY 50';
  const gap    = reference ? SGX.price - reference : 0;
  const gapPts = gap > 0 ? `+${gap.toFixed(0)}` : `${gap.toFixed(0)}`;
  const gapPct = reference ? (gap / reference * 100).toFixed(2) : '0.00';
  const pos    = gap >= 0;
  const label  = SGX.kiteConnected ? 'GIFT Nifty' : 'NIFTY 50';
  const sectionLabel = isPreOpen ? 'Pre-Market' : 'Post-Market';
  const badge        = isPreOpen ? 'PRE-OPEN'   : 'POST-CLOSE';

  return (
    <div style={{
      display:'flex', alignItems:'center', gap:12, flexShrink:0,
      padding:'8px 14px',
      background:'var(--surface-2)', border:'.5px solid var(--hair-strong)', borderRadius:8,
    }}>
      <div style={{fontSize:11,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',color:'var(--ink-3)'}}>
        {sectionLabel}
      </div>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <div style={{fontSize:11,fontWeight:700,background:'var(--dec)',color:'#fff',padding:'1px 5px',borderRadius:3,display:'inline-block'}}>{badge}</div>
        <span style={{fontSize:11,color:'var(--ink-3)'}}>{label}</span>
        <span style={{fontFamily:'var(--font-data)',fontWeight:700,fontSize:14,color:'var(--ink)'}}>{SGX.price.toLocaleString()}</span>
        <span style={{fontSize:11,fontFamily:'var(--font-data)',color:pos?'var(--up-field-ink)':'var(--down-field-ink)',fontWeight:600}}>
          {gapPts} pts ({pos?'+':''}{gapPct}%)
        </span>
      </div>
      <div style={{padding:'3px 10px',borderRadius:4,
        background:pos?'rgba(26,106,26,.10)':'rgba(136,30,16,.10)',
        border:`.5px solid ${pos?'rgba(26,106,26,.25)':'rgba(136,30,16,.25)'}`}}>
        <span style={{fontSize:11,fontWeight:700,color:pos?'var(--up-field-ink)':'var(--down-field-ink)'}}>
          {pos ? '▲ Gap Up' : '▼ Gap Down'}
        </span>
        <span style={{fontSize:11,color:'var(--ink-3)',marginLeft:6}}>vs {refLabel} {reference.toLocaleString()}</span>
      </div>
    </div>
  );
}

/* ── EIA ── */
function EIAWidget() {
  const {nextDate,nextTime,lastActual,tag,countdown,history}=EIA;
  const [editing, setEditing] = React.useState(false);
  const [drafts,  setDrafts]  = React.useState({});   // { period: "45.0" }
  const [saving,  setSaving]  = React.useState(false);

  // Only use rows that have actual data for bar chart
  const barData = history.filter(d => d.actual !== 0 || d.actual === 0).slice(0, 10);
  const maxAbs  = Math.max(1, ...barData.map(d =>
    Math.max(Math.abs(d.actual || 0), Math.abs(d.forecast || 0))
  ));
  const BAR_H = 160;

  const handleSave = async () => {
    setSaving(true);
    const entries = Object.entries(drafts).filter(([,v]) => v.trim() !== '');
    for (const [period, val] of entries) {
      const num = parseFloat(val);
      if (!isNaN(num)) {
        await saveEiaForecast(period, num);
      }
    }
    setSaving(false);
    setEditing(false);
    setDrafts({});
    // Reload EIA data
    getEiaCountdown().then(res => {
      const weeks = res.last_10_weeks || res.last_5_weeks || [];
      const history = weeks.map(w => {
        const actual     = w.change_bcf != null ? w.change_bcf : 0;
        const forecast   = w.forecast_bcf != null ? w.forecast_bcf : null;
        const surprise   = w.surprise_bcf != null ? w.surprise_bcf : null;
        const indication = w.indication || null;
        const bull       = actual < 0;
        const d          = w.period || '';
        const parts      = d.split('-');
        const months     = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const week       = parts.length === 3 ? `w/e ${months[+parts[1]-1]} ${parts[2]}` : d;
        return { week, actual, forecast, surprise, indication, bull, period: w.period };
      });
      EIA.history = history;
    }).catch(() => {});
  };

  const fmt = (v, prefix=true) => {
    if (v == null) return '—';
    return `${prefix && v > 0 ? '+' : ''}${v}`;
  };

  return (
    <div className="card" style={{padding:'10px 12px',marginBottom:6,borderLeft:'2px solid var(--dec)'}}>
      {/* Header row: title + date */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6,flexWrap:'wrap',gap:4}}>
        <div>
          <div style={{fontSize:11,fontWeight:600,color:'var(--ink)'}}>EIA Natural Gas Storage Report</div>
          <div style={{fontSize:11,color:'var(--ink-3)',marginTop:1}}>{nextDate} · {nextTime}</div>
        </div>
      </div>

      {/* Bold countdown + last reading */}
      <div style={{marginBottom:10}}>
        <div className="eia-countdown" style={{fontSize:26,fontWeight:700,color:'var(--amber)',fontFamily:'var(--font-data)',lineHeight:1.1}}>{countdown}</div>
        <div style={{fontSize:11,color:'var(--ink-3)',marginTop:4}}>
          Last: {lastActual>0?'+':''}{lastActual} Bcf ·{' '}
          <span style={{fontSize:11,fontWeight:700,padding:'1px 5px',borderRadius:3,
            background:tag==='Bullish'?'rgba(26,106,26,.10)':'rgba(136,30,16,.10)',
            color:tag==='Bullish'?'var(--up-field-ink)':'var(--down-field-ink)'}}>
            {tag==='Bullish'?'Bullish draw':'Bearish inject'}
          </span>
        </div>
      </div>

      {/* Legend */}
      <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:8}}>
        <span style={{fontSize:11,color:'var(--ink-3)',display:'flex',alignItems:'center',gap:4}}>
          <span style={{width:8,height:8,borderRadius:2,background:'var(--up-field-ink)',display:'inline-block'}}/>
          Bullish draw (storage ↓)
        </span>
        <span style={{fontSize:11,color:'var(--ink-3)',display:'flex',alignItems:'center',gap:4}}>
          <span style={{width:8,height:8,borderRadius:2,background:'var(--down-field-ink)',display:'inline-block'}}/>
          Bearish inject (storage ↑)
        </span>
      </div>

      <div className="eia-body" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,alignItems:'start'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:11,tableLayout:'fixed'}}>
          <thead>
            <tr>
              <th style={{textAlign:'left',color:'var(--ink-3)',fontWeight:600,padding:'3px 6px 4px',borderBottom:'.5px solid var(--hair)',fontSize:11,width:'32%'}}>Week</th>
              <th style={{textAlign:'right',color:'var(--ink-3)',fontWeight:600,padding:'3px 6px 4px',borderBottom:'.5px solid var(--hair)',fontSize:11,width:'20%'}}>Actual</th>
              <th style={{textAlign:'right',color:'var(--ink-3)',fontWeight:600,padding:'3px 6px 4px',borderBottom:'.5px solid var(--hair)',fontSize:11,width:'24%'}}>
                <span style={{display:'flex',alignItems:'center',justifyContent:'flex-end',gap:4}}>
                  Fcst
                  <a
                    href="https://in.investing.com/economic-calendar/natural-gas-storage-386"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{color:'var(--amber-ink)',opacity:0.65,fontSize:11,lineHeight:1,textDecoration:'none'}}
                    title="View on Investing.com"
                  >↗</a>
                </span>
              </th>
              <th style={{textAlign:'right',color:'var(--ink-3)',fontWeight:600,padding:'3px 6px 4px',borderBottom:'.5px solid var(--hair)',fontSize:11,width:'24%'}}>Surprise</th>
            </tr>
          </thead>
          <tbody>
            {history.map((d,i) => {
              const surprise    = d.surprise;
              const forecastVal = editing
                ? (drafts[d.period] !== undefined ? drafts[d.period] : (d.forecast != null ? String(d.forecast) : ''))
                : d.forecast;
              return (
                <tr key={i} style={{borderBottom:'.5px solid var(--hair)'}}>
                  <td style={{padding:'4px 6px',color:'var(--ink-3)',fontSize:11,textAlign:'left'}}>{d.week}</td>
                  <td style={{padding:'4px 6px',fontFamily:'var(--font-data)',fontWeight:600,color:'var(--ink)',textAlign:'right'}}>
                    {fmt(d.actual)}
                  </td>
                  <td style={{padding:'2px 4px',textAlign:'right'}}>
                    {editing ? (
                      <input
                        type="number"
                        step="0.1"
                        value={drafts[d.period] !== undefined ? drafts[d.period] : (d.forecast != null ? String(d.forecast) : '')}
                        onChange={e => setDrafts(prev => ({...prev, [d.period]: e.target.value}))}
                        style={{
                          width:'100%', textAlign:'right', fontSize:11, padding:'2px 4px',
                          background:'var(--surface-2)', border:'.5px solid var(--hair-strong)',
                          borderRadius:3, color:'var(--ink)', fontFamily:'var(--font-data)',
                        }}
                        placeholder="—"
                      />
                    ) : (
                      <span style={{fontFamily:'var(--font-data)',color:'var(--ink-2)',fontSize:11}}>
                        {fmt(forecastVal)}
                      </span>
                    )}
                  </td>
                  <td style={{padding:'4px 6px',fontFamily:'var(--font-data)',
                    color: surprise == null ? 'var(--ink-3)' : surprise < 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
                    textAlign:'right', fontSize:11, fontWeight: surprise != null ? 700 : 400}}>
                    {fmt(surprise)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{display:'flex',flexDirection:'column'}}>
          {editing && (
            <div style={{marginBottom:8,display:'flex',gap:6,justifyContent:'flex-end'}}>
              <button
                onClick={()=>{setEditing(false);setDrafts({});}}
                style={{fontSize:11,padding:'3px 10px',borderRadius:4,border:'.5px solid var(--hair-strong)',
                  background:'var(--surface-2)',color:'var(--ink-2)',cursor:'pointer'}}>
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{fontSize:11,padding:'3px 10px',borderRadius:4,border:'none',
                  background:'var(--amber-ink)',color:'#fff',cursor:'pointer',fontWeight:600}}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
          <div style={{fontSize:11,color:'var(--ink-3)',marginBottom:4}}>Actual (solid) · Forecast (faded) · Below zero = draw</div>
          <div style={{display:'flex',gap:3}}>
            {barData.map((d,i) => {
              const zero = BAR_H / 2;
              const aH   = Math.max(3, Math.abs(d.actual || 0) / maxAbs * zero);
              const fH   = d.forecast != null ? Math.max(3, Math.abs(d.forecast) / maxAbs * zero) : 0;
              const col  = d.indication === 'Bullish' ? 'var(--up-field-ink)' : d.indication === 'Bearish' ? 'var(--down-field-ink)' : d.bull ? 'var(--up-field-ink)' : '#888888';
              const act  = d.actual || 0;
              return (
                <div key={i} style={{flex:1,minWidth:0}}>
                  <div style={{position:'relative',height:BAR_H,width:'100%'}}>
                    {/* zero line */}
                    <div style={{position:'absolute',top:zero,left:0,right:0,height:.5,background:'var(--hair-strong)'}}/>
                    {/* actual bar: injection (>=0) goes up, draw (<0) goes down */}
                    {act >= 0
                      ? <div style={{position:'absolute',bottom:zero,left:'20%',width:'22%',height:aH,background:col,borderRadius:'2px 2px 0 0'}}/>
                      : <div style={{position:'absolute',top:zero,   left:'20%',width:'22%',height:aH,background:col,borderRadius:'0 0 2px 2px'}}/>}
                    {/* forecast bar: same logic, offset right */}
                    {fH > 0 && (d.forecast >= 0
                      ? <div style={{position:'absolute',bottom:zero,left:'55%',width:'22%',height:fH,background:col,opacity:.3,borderRadius:'2px 2px 0 0'}}/>
                      : <div style={{position:'absolute',top:zero,   left:'55%',width:'22%',height:fH,background:col,opacity:.3,borderRadius:'0 0 2px 2px'}}/>)}
                  </div>
                  <div style={{fontSize:11,color:'var(--ink-3)',whiteSpace:'nowrap',textAlign:'center'}}>{d.week?.split(' ')[1]||''}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}



/* ── Breadth — hardcoded colours ── */
const ADV='#40a8d8', DEC='#e08838';
function _fmtBarDate(iso) {
  if (!iso) return '';
  const [,m,d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(d,10)}-${months[parseInt(m,10)-1]}`;
}
/* BreadthRow — horizontal progress bar only */
function BreadthRow({label, data}) {
  const {today} = data;
  const total  = (today.adv + today.dec) || 1;
  const advPct = (today.adv / total * 100).toFixed(0);
  return (
    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
      <div style={{fontSize:11,color:'var(--ink-2)',minWidth:90,fontWeight:500}}>{label}</div>
      <div style={{display:'flex',alignItems:'center',gap:5,flex:1}}>
        <span style={{fontSize:11,fontWeight:700,minWidth:36,textAlign:'right',fontFamily:'var(--font-data)',color:ADV}}>{today.adv}▲</span>
        <div style={{flex:1,height:7,borderRadius:4,overflow:'hidden',minWidth:60,display:'flex'}}>
          <div style={{width:`${advPct}%`,height:'100%',background:ADV}}/>
          <div style={{flex:1,height:'100%',background:DEC}}/>
        </div>
        <span style={{fontSize:11,fontWeight:700,minWidth:36,fontFamily:'var(--font-data)',color:DEC}}>▼{today.dec}</span>
      </div>
    </div>
  );
}

/* BreadthHistChart — shared bar chart to the right of both breadth rows */
function BreadthHistChart({hist}) {
  const [hoveredBar, setHoveredBar] = useState(null);
  if (!hist || hist.length === 0) return null;
  return (
    <div style={{borderLeft:'.5px solid var(--hair)',paddingLeft:8,flexShrink:0}}>
      <div style={{position:'relative',display:'flex',alignItems:'flex-end',gap:3,height:56,width:120}}>
        {hist.map((d, i) => {
          const rawAdv     = d?.adv ?? 0;
          const rawDec     = d?.dec ?? 0;
          const hasData    = rawAdv > 0 || rawDec > 0;
          const isLive     = d?.live === true;   // only true when market is open
          const isLast     = i === hist.length - 1;
          const tooltipLeft = i >= hist.length - 3;
          return (
            <div
              key={i}
              style={{
                flex:1, height:50, borderRadius:3, overflow:'visible',
                display:'flex', flexDirection:'column', position:'relative',
                cursor: hasData ? 'default' : 'not-allowed',
              }}
              onMouseEnter={() => setHoveredBar(i)}
              onMouseLeave={() => setHoveredBar(null)}
            >
              {/* Hover tooltip */}
              {hoveredBar === i && hasData && (
                <div style={{
                  position:'absolute', bottom:'calc(100% + 6px)',
                  left: tooltipLeft ? 'auto' : '50%',
                  right: tooltipLeft ? 0 : 'auto',
                  transform: tooltipLeft ? 'none' : 'translateX(-50%)',
                  background:'var(--surface)', border:'.5px solid var(--hair-strong)',
                  borderRadius:5, padding:'5px 9px', whiteSpace:'nowrap',
                  zIndex:200, boxShadow:'0 2px 10px rgba(0,0,0,.18)', pointerEvents:'none',
                }}>
                  <div style={{fontSize:11,color:'var(--ink-3)',fontWeight:600,marginBottom:3}}>
                    {d?.date ? _fmtBarDate(d.date) : '—'}
                    {isLive && <span style={{marginLeft:4,fontSize:11,color:'var(--amber-ink)',fontWeight:700}}>LIVE</span>}
                  </div>
                  <div style={{fontSize:11,fontFamily:'var(--font-data)'}}>
                    <span style={{color:ADV}}>▲{rawAdv}</span>
                    <span style={{color:'var(--ink-3)',margin:'0 5px'}}>/</span>
                    <span style={{color:DEC}}>▼{rawDec}</span>
                  </div>
                </div>
              )}
              {/* Bar fill */}
              {hasData ? (
                <>
                  <div style={{flex:d?.d||1, background:DEC, minHeight:2, borderRadius:'3px 3px 0 0', opacity: isLast ? 1 : 0.7}}/>
                  <div style={{flex:d?.a||1, background:ADV, minHeight:2, borderRadius:'0 0 3px 3px', opacity: isLast ? 1 : 0.7}}/>
                </>
              ) : (
                <div style={{width:'100%',height:'100%',background:'var(--hair)',borderRadius:3,opacity:0.25}}/>
              )}
              {/* "now" label — only when market is live */}
              {isLive && (
                <div style={{
                  position:'absolute', bottom:-11, left:'50%', transform:'translateX(-50%)',
                  fontSize:11, color:'var(--amber-ink)', fontWeight:600, letterSpacing:'0.02em',
                }}>now</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Heatmap — 4 cols, rgba gradient ── */
function HeatGrid({title,items}) {
  const { open: openPanel } = useSidePanel();
  const [sort,setSort]=useState('pct');
  const sorted=[...items].sort((a,b)=>sort==='pct'?b.pct-a.pct:a.name.localeCompare(b.name));
  return (
    <div className="card" style={{padding:'9px 10px',marginBottom:6}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:7}}>
        <span style={{fontSize:11,fontWeight:600,color:'var(--ink)'}}>{title}</span>
        <div style={{display:'flex',gap:3}}>
          {[['pct','% Chg'],['az','A–Z']].map(([v,l])=>(
            <button key={v} onClick={()=>setSort(v)} style={{fontSize:11,padding:'2px 7px',borderRadius:10,border:'.5px solid var(--hair-strong)',color:sort===v?'var(--ink)':'var(--ink-3)',background:sort===v?'var(--surface-3)':'transparent',cursor:'pointer'}}>{l}</button>
          ))}
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,minmax(0,1fr))',gap:4}}>
        {sorted.map(item=>{
          const s=heatStyle(item.pct);
          const kiteSymbol = DISPLAY_TO_KITE_SYMBOL[item.name] || item.name;
          return (
            <div key={item.name}
              onClick={()=>openPanel({ name: kiteSymbol })}
              style={{...s,borderRadius:10,padding:'9px 6px',textAlign:'center',cursor:'pointer',minHeight:54}}>
              <div style={{fontSize:11,fontWeight:500,lineHeight:1.2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.name}</div>
              <div style={{fontSize:13,fontWeight:600,marginTop:3,fontFamily:'var(--font-data)'}}>{item.pct>=0?'+':''}{item.pct.toFixed(1)}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Movers — deep green/red, data-driven volume widths ── */
function parseVol(v) {
  if (!v) return 0;
  const s = String(v).trim();
  if (s.endsWith('M')) return parseFloat(s) * 1e6;
  if (s.endsWith('K')) return parseFloat(s) * 1e3;
  return parseFloat(s) || 0;
}

// Compact sector abbreviations for top F&O stocks
const FO_SECTOR = {
  RELIANCE:'Energy', TCS:'IT', INFY:'IT', HDFCBANK:'Bank', ICICIBANK:'Bank',
  KOTAKBANK:'Bank', AXISBANK:'Bank', SBIN:'Bank', INDUSINDBK:'Bank', BANDHANBNK:'Bank',
  FEDERALBNK:'Bank', IDFCFIRSTB:'Bank', RBLBANK:'Bank', AUBANK:'Bank',
  HDFC:'Finance', BAJFINANCE:'Finance', BAJAJFINSV:'Finance', MUTHOOTFIN:'Finance',
  CHOLAFIN:'Finance', M_MFIN:'Finance', SHRIRAMFIN:'Finance', PFC:'Finance', RECLTD:'Finance',
  HCLTECH:'IT', WIPRO:'IT', TECHM:'IT', LTI:'IT', LTIM:'IT', MPHASIS:'IT', COFORGE:'IT', PERSISTENT:'IT',
  HINDUNILVR:'FMCG', ITC:'FMCG', NESTLEIND:'FMCG', BRITANNIA:'FMCG', DABUR:'FMCG',
  MARICO:'FMCG', GODREJCP:'FMCG', TATACONSUM:'FMCG', COLPAL:'FMCG',
  TATAMOTORS:'Auto', MARUTI:'Auto', BAJAJ_AUTO:'Auto', HEROMOTOCO:'Auto', EICHERMOT:'Auto',
  MOTHERSON:'Auto', BALKRISIND:'Auto', BOSCHLTD:'Auto', MRF:'Auto', APOLLOTYRE:'Auto',
  TATASTEEL:'Metal', HINDALCO:'Metal', JSWSTEEL:'Metal', SAIL:'Metal', COALINDIA:'Metal',
  VEDL:'Metal', NATIONALUM:'Metal', NMDC:'Metal',
  SUNPHARMA:'Pharma', DRREDDY:'Pharma', CIPLA:'Pharma', DIVISLAB:'Pharma', AUROPHARMA:'Pharma',
  TORNTPHARM:'Pharma', BIOCON:'Pharma', ALKEM:'Pharma', LUPIN:'Pharma', GLENMARK:'Pharma',
  POWERGRID:'Power', NTPC:'Power', ADANIPOWER:'Power', TATAPOWER:'Power', CESC:'Power',
  ONGC:'Oil&Gas', BPCL:'Oil&Gas', IOC:'Oil&Gas', GAIL:'Oil&Gas', OIL:'Oil&Gas', MGL:'Oil&Gas',
  ADANIENT:'Infra', ADANIPORTS:'Infra', L_T:'Infra', GMRINFRA:'Infra', IRB:'Infra',
  ASIANPAINT:'Paint', BERGEPAINT:'Paint', KANSAINER:'Paint',
  ULTRACEMCO:'Cement', SHREECEM:'Cement', AMBUJACEM:'Cement', ACC:'Cement', GRASIM:'Cement',
  TITAN:'Jewel', JUBLFOOD:'Food', ZOMATO:'Food', NAUKRI:'Tech', DMART:'Retail',
  SBICARD:'Finance', HDFCLIFE:'Insure', SBILIFE:'Insure', ICICIGI:'Insure', LICI:'Insure',
  IRCTC:'PSU', BHEL:'PSU', HAL:'Defence', BEL:'Defence', COCHINSHIP:'Defence',
  INDHOTEL:'Hotel', LEMONTREE:'Hotel', EIHOTEL:'Hotel',
  IDEA:'Telecom', BHARTIARTL:'Telecom',
  OBEROIRLTY:'Realty', DLF:'Realty', GODREJPROP:'Realty', PRESTIGE:'Realty', SOBHA:'Realty',
};

function Movers() {
  const { open: openPanel } = useSidePanel();
  const [tab,setTab]=useState('gainers');
  const maxG=FO_MOVERS.gainers[0]?.pct||1;
  const maxL=Math.abs(FO_MOVERS.losers[0]?.pct||1);
  const maxVol=Math.max(...FO_MOVERS.volume.map(d=>parseVol(d.vol)));
  const isHolidayVol = FO_MOVERS.volume.length > 0 && maxVol === 0;
  const Panel=({items,isVol})=>{
    if (isVol && isHolidayVol) return (
      <div style={{height:140,display:'flex',alignItems:'center',justifyContent:'center'}}>
        <span style={{fontSize:11,color:'var(--ink-3)',textAlign:'center',lineHeight:1.6}}>
          No volume data<br/>Market holiday
        </span>
      </div>
    );
    return (
    <div style={{height:140,overflowY:'auto'}}>
      {items.map((d,i)=>{
        const pos=isVol||d.pct>=0;
        const w=isVol
          ? Math.round(parseVol(d.vol)/maxVol*90)
          : Math.round(Math.abs(d.pct||0)/(pos?maxG:maxL)*90);
        const fill=isVol?'rgba(60,80,110,.55)':pos?'rgba(26,106,26,.55)':'rgba(136,30,16,.55)';
        const tc=isVol?'#fff':pos?'#e8f5e8':'#ffe8e8';
        const sector = FO_SECTOR[d.name] || d.sector || null;
        return (
          <div key={i} style={{display:'flex',alignItems:'center',gap:4,marginBottom:5}}>
            <span style={{fontSize:11,fontWeight:600,minWidth:46,textAlign:'right',fontFamily:'var(--font-data)',color:isVol?'var(--ink-2)':pos?'var(--up-field-ink)':'var(--down-field-ink)'}}>{isVol?d.vol:(pos?'+':'')+d.pct?.toFixed(2)+'%'}</span>
            <div style={{flex:1,height:16,borderRadius:2,background:'var(--surface-2)',overflow:'hidden'}}>
              <div style={{width:`${Math.max(10,w)}%`,height:'100%',background:fill,display:'flex',alignItems:'center',gap:5,paddingLeft:5}}>
                <span
                  onClick={()=>openPanel({ name: d.name })}
                  style={{fontSize:11,fontWeight:600,color:tc,whiteSpace:'nowrap',cursor:'pointer'}}
                >{d.name}</span>
                {sector && !isVol && (
                  <span style={{fontSize:11,fontWeight:500,color:pos?'rgba(200,240,200,.75)':'rgba(255,210,210,.75)',whiteSpace:'nowrap',letterSpacing:'0.02em'}}>
                    {sector}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
    );
  };
  return (
    <div style={{marginBottom:6}}>
      <div className="desktop-only" style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
        {[{t:'Top Gainers',items:FO_MOVERS.gainers},{t:'Top Losers',items:FO_MOVERS.losers},{t:'Most Active',items:FO_MOVERS.volume,vol:true}].map(({t,items,vol})=>(
          <div key={t} className="card" style={{padding:'9px'}}>
            <div style={{fontSize:11,fontWeight:600,color:'var(--ink)',marginBottom:7}}>{t}</div>
            <Panel items={items} isVol={vol}/>
          </div>
        ))}
      </div>
      <div className="mobile-only card" style={{padding:'9px'}}>
        <div style={{display:'flex',gap:5,marginBottom:8}}>
          {[['gainers','Gainers'],['losers','Losers'],['volume','Volume']].map(([v,l])=>(
            <button key={v} className={`pill${tab===v?' on':''}`} onClick={()=>setTab(v)}>{l}</button>
          ))}
        </div>
        {tab==='gainers'&&<Panel items={FO_MOVERS.gainers}/>}
        {tab==='losers'&&<Panel items={FO_MOVERS.losers}/>}
        {tab==='volume'&&<Panel items={FO_MOVERS.volume} isVol/>}
      </div>
    </div>
  );
}

/* ── 52W ── */
function Extremes() {
  const {open:openPanel}=useSidePanel();
  const _today = new Date();
  const _mo = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const _todayFmt = `${_today.getDate()}-${_mo[_today.getMonth()]}-${String(_today.getFullYear()).slice(2)}`;
  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:7,marginBottom:6}}>
      {[{t:'52-Week Highs',col:'var(--up-field-ink)',data:EXTREMES.highs},{t:'52-Week Lows',col:'var(--down-field-ink)',data:EXTREMES.lows}].map(({t,col,data})=>(
        <div key={t} className="card" style={{padding:'8px 10px'}}>
          <div style={{fontSize:11,fontWeight:600,color:col,marginBottom:4}}>{t} ({data.length})</div>
          <div style={{maxHeight:110,overflowY:'auto'}}>
            {data.length === 0 ? (
              <div style={{fontSize:11,color:'var(--ink-3)',fontStyle:'italic',padding:'6px 2px'}}>
                none today ({_todayFmt})
              </div>
            ) : data.map(d=>(
              <div key={d.s} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'2px 0',borderBottom:'.5px solid var(--hair)'}}>
                <span className="tlink" style={{fontSize:11}} onClick={()=>openPanel({name:d.s})}>{d.s}</span>
                <div style={{display:'flex',gap:8,alignItems:'center'}}>
                  <span style={{fontFamily:'var(--font-data)',fontSize:11,color:'var(--ink)'}}>₹{d.price}</span>
                  <span style={{fontFamily:'var(--font-data)',fontSize:11,color:(d.chgPct||0)>=0?'var(--up-field-ink)':'var(--down-field-ink)',minWidth:44,textAlign:'right'}}>{(d.chgPct||0)>=0?'+':''}{(d.chgPct||0).toFixed(2)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Digest & News (unchanged) ── */
const CAT_CLS={'Macro':'#eeedfe:#3838a0','Energy':'#faecd8:#7a3e00','Global':'#eaf4ea:#1a6a1a','FII Flows':'#d8f4ec:#0a5a40','Indian Equity':'#daeeff:#1060a0'};
const ALL_CATS=['All','Indian Equity','Natural Gas & Energy','Macro','Global Markets','FII Flows','Geopolitical','NSE/BSE Circulars'];
const ICON_BG={'Indian Equity':'#daeeff','Natural Gas & Energy':'#faecd8','Macro':'#eeedfe','Global Markets':'#eaf4ea','FII Flows':'#d8f4ec','Geopolitical':'#faecd8','NSE/BSE Circulars':'var(--surface-3)'};

// Sentiment label → colours (matches AISummaryTab SENTIMENT_STYLE)
const SENTIMENT_STYLE = {
  Bullish:  { bg:'var(--up-field-bg)',   c:'var(--up-field-ink)',   border:'var(--up-field-bg-2)',   dot:'var(--up-field-ink)'   },
  Bearish:  { bg:'var(--down-field-bg)', c:'var(--down-field-ink)', border:'var(--down-field-bg-2)', dot:'var(--down-field-ink)' },
  Cautious: { bg:'var(--amber-bg)',      c:'var(--amber-ink)',      border:'var(--amber-hair)',       dot:'var(--amber-ink)'      },
  Neutral:  { bg:'var(--surface-2)',     c:'var(--ink-2)',          border:'var(--hair-strong)',      dot:'var(--ink-3)'          },
};

function DigestList({ items }) {
  return (
    <ul style={{listStyle:'none',padding:0,margin:0}}>
      {items.map((d,i)=>{
        const [bg,c]=(CAT_CLS[d.cat]||'var(--surface-3):var(--ink-2)').split(':');
        return (
          <li key={i} style={{display:'flex',alignItems:'flex-start',gap:7,marginBottom:5,fontSize:11,color:'var(--ink-2)',lineHeight:1.55}}>
            <span style={{flexShrink:0,marginTop:2}}>•</span>
            <span style={{fontSize:11,fontWeight:700,padding:'1px 5px',borderRadius:3,background:bg,color:c,flexShrink:0,marginTop:3,alignSelf:'flex-start'}}>{d.cat}</span>
            <span>{d.text}</span>
          </li>
        );
      })}
    </ul>
  );
}

// Direction pill for news/overnight items
const DIR_STYLE = {
  bullish: { bg:'rgba(26,106,26,.12)', c:'var(--up-field-ink)',   sym:'▲' },
  bearish: { bg:'rgba(136,30,16,.12)', c:'var(--down-field-ink)',   sym:'▼' },
  neutral: { bg:'var(--surface-3)',          c:'var(--ink-3)',sym:'–'  },
};

function DirPill({ direction }) {
  const d = DIR_STYLE[direction] || DIR_STYLE.neutral;
  return (
    <span style={{fontSize:11,fontWeight:700,padding:'1px 5px',borderRadius:3,
      background:d.bg,color:d.c,flexShrink:0,marginTop:3,alignSelf:'flex-start',
      letterSpacing:'.03em'}}>
      {d.sym}
    </span>
  );
}

function DigestListWithDir({ items }) {
  return (
    <ul style={{listStyle:'none',padding:0,margin:0}}>
      {items.map((d,i)=>{
        const [bg,c]=(CAT_CLS[d.cat]||'var(--surface-3):var(--ink-2)').split(':');
        return (
          <li key={i} style={{display:'flex',alignItems:'flex-start',gap:5,marginBottom:5,
            fontSize:11,color:'var(--ink-2)',lineHeight:1.55}}>
            {d.direction && <DirPill direction={d.direction}/>}
            <span style={{fontSize:11,fontWeight:700,padding:'1px 5px',borderRadius:3,background:bg,color:c,
              flexShrink:0,marginTop:3,alignSelf:'flex-start'}}>{d.cat}</span>
            <span>{d.text}</span>
          </li>
        );
      })}
    </ul>
  );
}

// Fmt IST timestamp from UTC DB string
function fmtGenTime(isoUtc) {
  if (!isoUtc) return null;
  try {
    const d = new Date(isoUtc.replace(' ', 'T') + 'Z');
    return d.toLocaleTimeString('en-IN', { timeZone:'Asia/Kolkata', hour:'2-digit', minute:'2-digit', hour12:false }) + ' IST';
  } catch { return null; }
}

function Digest() {
  const [report,     setReport]     = useState(null);
  const [overnight,  setOvernight]  = useState(MOCK_OVERNIGHT);
  const [genTime,    setGenTime]    = useState(null);   // last generated timestamp
  const [generating, setGenerating] = useState(false);
  const [genMsg,     setGenMsg]     = useState('');

  // Sync from module-level cache every 10s
  useEffect(() => {
    function sync() {
      if (!_OVERNIGHT_DATA) { setReport(null); setOvernight(MOCK_OVERNIGHT); return; }
      if (Array.isArray(_OVERNIGHT_DATA)) {
        setOvernight(_OVERNIGHT_DATA);
        setReport(null);
      } else {
        setReport(_OVERNIGHT_DATA);
        setOvernight(_OVERNIGHT_DATA.overnight || []);
      }
    }
    sync();
    const iv = setInterval(sync, 10_000);
    return () => clearInterval(iv);
  }, []);

  // Fetch report data + timestamp on mount (ensures mobile sees data without waiting for 5-min poll)
  useEffect(() => {
    getTabReports('dashboard', 1).then(res => {
      const latest = res.reports?.[0];
      if (!latest) return;
      if (latest?.updated_at || latest?.created_at) {
        setGenTime(fmtGenTime(latest.updated_at || latest.created_at));
      }
      if (latest?.report_json) {
        let parsed = {};
        try { parsed = typeof latest.report_json === 'string' ? JSON.parse(latest.report_json) : latest.report_json; } catch {}
        if (parsed && typeof parsed === 'object') {
          _OVERNIGHT_DATA = parsed;
          setReport(parsed);
          setOvernight(parsed.overnight || []);
        }
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  const refreshFromDB = async () => {
    try {
      const res = await getTabReports('dashboard', 1);
      const latest = res.reports?.[0];
      if (latest?.report_json) {
        let parsed = {};
        try { parsed = typeof latest.report_json === 'string' ? JSON.parse(latest.report_json) : latest.report_json; } catch {}
        _OVERNIGHT_DATA = parsed;
        setReport(parsed);
        setOvernight(parsed.overnight || []);
        setGenTime(fmtGenTime(latest.updated_at || latest.created_at));
      }
    } catch {}
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenMsg('Generating… (20–60s)');
    const prevDate = report?.date || '';
    try {
      await generateTabReport('dashboard', null);
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const res = await getTabReports('dashboard', 1);
          const latest = res.reports?.[0];
          if (latest) {
            const newDate = latest.report_date || '';
            if (attempts > 3 || newDate !== prevDate) {
              clearInterval(poll);
              await refreshFromDB();
              setGenerating(false);
              setGenMsg('✓ Generated!');
              setTimeout(() => setGenMsg(''), 4000);
              return;
            }
          }
        } catch {}
        if (attempts >= 25) {
          clearInterval(poll);
          setGenerating(false);
          setGenMsg('⚠ Timed out — refresh in a minute');
        }
      }, 6000);
    } catch (e) {
      setGenerating(false);
      setGenMsg(`Error: ${e.message}`);
    }
  };

  const sentiment  = report?.sentiment || null;
  const newsDigest = report?.news_digest || [];
  const hasLive    = !!(report || (Array.isArray(_OVERNIGHT_DATA) ? _OVERNIGHT_DATA.length : false));
  const ss         = sentiment ? (SENTIMENT_STYLE[sentiment.label] || SENTIMENT_STYLE.Neutral) : null;

  return (
    <div className="card" style={{padding:'9px 12px',marginBottom:7,borderLeft:'2px solid var(--dec)'}}>

      {/* ── Section 1: Bias — header row with Generate inline, then 3-col layout ── */}
      <div style={{marginBottom:10}}>
        {/* Header: "📊 Bias" label + status + timestamp + generate button (all on one row) */}
        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:7,flexWrap:'wrap'}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:'.06em',textTransform:'uppercase',color:'var(--ink-3)'}}>📊 Bias</div>
          <div style={{flex:1}}/>
          {genMsg && (
            <span style={{fontSize:11,fontWeight:600,
              color:genMsg.startsWith('✓')?'var(--up-field-ink)':genMsg.startsWith('⚠')?'var(--amber-ink)':'var(--ink-2)'}}>
              {genMsg}
            </span>
          )}
          {genTime && !genMsg && <span style={{fontSize:11,color:'var(--ink-3)'}}>Generated {genTime}</span>}
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:4,
              border:'.5px solid var(--hair-strong)',background:'var(--surface-2)',color:'var(--ink-2)',
              cursor:generating?'default':'pointer',opacity:generating?0.6:1}}>
            {generating ? '⏳' : '⚡ Generate'}
          </button>
        </div>

        {/* Bias body: left col = label headline, right = bullet grid */}
        {sentiment && ss ? (
          <div style={{display:'flex',gap:0,borderRadius:6,background:ss.bg,
            border:`.5px solid ${ss.border}`,overflow:'hidden'}}>
            {/* Left column: bias label as headline */}
            <div style={{padding:'10px 14px',borderRight:`.5px solid ${ss.border}`,
              display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center',
              minWidth:72,flexShrink:0}}>
              <span style={{width:8,height:8,borderRadius:'50%',background:ss.c,
                display:'block',marginBottom:7,flexShrink:0}}/>
              <div style={{fontSize:16,fontWeight:800,color:ss.c,letterSpacing:'.02em',
                textAlign:'center',lineHeight:1.15,textTransform:'uppercase'}}>
                {sentiment.label}
              </div>
            </div>
            {/* Right: bullets in up-to-2-column grid */}
            <div style={{flex:1,padding:'10px 12px'}}>
              {(() => {
                const bullets = sentiment.text.split(/(?<=\.)\s+/).filter(Boolean);
                if (bullets.length === 0) return (
                  <div style={{fontSize:11,color:'var(--ink)',lineHeight:1.55}}>{sentiment.text}</div>
                );
                const cols = bullets.length === 1 ? 1 : 2;
                return (
                  <div style={{display:'grid',gridTemplateColumns:`repeat(${cols},1fr)`,gap:6}}>
                    {bullets.map((b,i) => (
                      <div key={i} style={{display:'flex',gap:5,alignItems:'flex-start'}}>
                        <span style={{color:ss.c,flexShrink:0,fontWeight:700,fontSize:11,marginTop:1}}>›</span>
                        <span style={{fontSize:11,color:'var(--ink)',lineHeight:1.5}}>{b}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        ) : (
          <div style={{fontSize:11,color:'var(--ink-3)',padding:'8px 0',fontStyle:'italic'}}>
            No bias data yet — hit ⚡ Generate to run today's report.
          </div>
        )}
      </div>

      {/* ── Section 2: News Digest (48h) ── */}
      {newsDigest.length > 0 && (
        <div style={{marginBottom:10,paddingTop:8,borderTop:'.5px solid var(--hair)'}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:'.06em',textTransform:'uppercase',
            color:'var(--ink-3)',marginBottom:5}}>📰 News — Last 48h</div>
          <DigestListWithDir items={newsDigest}/>
        </div>
      )}

      {/* ── Section 3: Overnight (6pm–8:30am) ── */}
      <div style={{paddingTop:(sentiment||newsDigest.length)?8:0,
        borderTop:(sentiment||newsDigest.length)?'.5px solid var(--hair)':'none'}}>
        <div style={{fontSize:11,fontWeight:700,letterSpacing:'.06em',textTransform:'uppercase',
          color:'var(--ink-3)',marginBottom:5}}>🌙 Overnight (6pm–8:30am)</div>
        {overnight.length > 0
          ? <DigestListWithDir items={overnight}/>
          : <div style={{fontSize:11,color:'var(--ink-3)'}}>No overnight data yet.</div>}
      </div>
    </div>
  );
}

/* ── Impact config (key = backend value, label = display) ── */
const IMPACT_META = {
  HIGH:   { label:'High',   dot:'🔴', bg:'var(--down-field-bg)',  color:'var(--down-field-ink)', border:'var(--down-field-bg-2)' },
  MEDIUM: { label:'Medium', dot:'🟡', bg:'var(--amber-bg)',        color:'var(--amber-ink)',       border:'var(--amber-hair)'      },
  IGNORE: { label:'Low',    dot:'⚫', bg:'var(--surface-2)',       color:'var(--ink-2)',           border:'var(--hair-strong)'     },
};
const ALL_IMPACTS = ['HIGH','MEDIUM','IGNORE'];

// CSS radial-gradient ball — used for Unclassified (grey) and inline dots
function Ball({ gradient, size=9 }) {
  return (
    <span style={{
      display:'inline-block', width:size, height:size, borderRadius:'50%',
      background:gradient, flexShrink:0, verticalAlign:'middle',
    }}/>
  );
}
const GREY_BALL  = 'radial-gradient(circle at 35% 35%, #d1d5db, #6b7280)';

function ImpactPill({ impact, size=9 }) {
  if (!impact) return null;
  const m = IMPACT_META[impact];
  if (!m) return null;
  return (
    <span title={impact} style={{
      display:'inline-flex', alignItems:'center', gap:3,
      fontSize:size, fontWeight:700, padding:'1px 5px', borderRadius:3,
      background:m.bg, color:m.color, border:`.5px solid ${m.border}`,
      flexShrink:0, letterSpacing:'.02em',
    }}>
      {m.dot} {m.label}
    </span>
  );
}

/* ── Impact filter button ── */
function ImpactBtn({ impKey, label, dot, on, bg, color, border, onClick }) {
  return (
    <button onClick={onClick} style={{
      display:'inline-flex', alignItems:'center', gap:3,
      fontSize:11, padding:'2px 8px', borderRadius:10, cursor:'pointer',
      fontWeight: on ? 700 : 400,
      border: `.5px solid ${on ? border : 'var(--hair-strong)'}`,
      background: on ? bg : 'var(--surface)',
      color: on ? color : 'var(--ink-3)',
      opacity: on ? 1 : 0.5,
      transition:'all .15s',
    }}>
      {typeof dot === 'string' ? dot : dot}
      {' '}{label}
    </button>
  );
}

function NewsFeed() {
  const [catFilter,    setCatFilter]    = useState('All');
  // impactFilter: Set of active impact keys. '' = Unclassified. All active by default.
  const [impactFilter, setImpactFilter] = useState(new Set(['HIGH','MEDIUM','']));
  const [classified,   setClassified]   = useState({});

  // Sync module-level classified map (updated by 5-min poll) into React state every 10 s
  useEffect(() => {
    function sync() { setClassified({ ..._CLASSIFIED_MAP }); }
    sync();
    const iv = setInterval(sync, 10_000);
    return () => clearInterval(iv);
  }, []);

  const toggleImpact = (key) => {
    setImpactFilter(prev => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      if (next.size === 0) return prev; // always keep at least one active
      return next;
    });
  };

  // Enrich full news pool with classified impact — matched by item_id (exact, no title fragility)
  const enriched = NEWS.map(n => ({
    ...n,
    impact:    classified[n.id]?.impact    || '',
    rationale: classified[n.id]?.rationale || '',
  }));

  // ALL_ACTIVE = all four buckets selected → default chronological top-20 view
  const ALL_ACTIVE =
    impactFilter.has('HIGH') && impactFilter.has('MEDIUM') &&
    impactFilter.has('IGNORE') && impactFilter.has('');

  // Counts across full pool (not just displayed 20) so buttons always show real totals
  const counts = { HIGH:0, MEDIUM:0, IGNORE:0, '':0 };
  enriched.forEach(n => { const k = n.impact || ''; if (k in counts) counts[k]++; });

  // Apply category filter first (always), then impact filter
  const catFiltered = catFilter === 'All' ? enriched : enriched.filter(n => n.cat === catFilter);

  // All active → top 20 overall (chronological); otherwise filter by impact then cap at 20
  const items = ALL_ACTIVE
    ? catFiltered.slice(0, 20)
    : catFiltered.filter(n => impactFilter.has(n.impact || '')).slice(0, 20);

  return (
    <div className="card" style={{padding:'9px 12px'}}>
      {/* ── Header ── */}
      <div style={{marginBottom:8}}>
        <span style={{fontSize:11,fontWeight:600,color:'var(--ink)'}}>News Feed — Last 7 Days</span>
      </div>

      {/* ── Filter row: category LEFT, impact RIGHT ── */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8,marginBottom:9,flexWrap:'wrap'}}>

        {/* Left — category single-select (unchanged) */}
        <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
          {ALL_CATS.map(c=>(
            <button key={c} onClick={()=>setCatFilter(c)} style={{
              fontSize:11, padding:'3px 8px', borderRadius:10,
              border:'.5px solid var(--hair-strong)', cursor:'pointer',
              color:catFilter===c?'var(--amber-ink)':'var(--ink-2)',
              background:catFilter===c?'var(--amber-bg)':'var(--surface)',
            }}>{c}</button>
          ))}
        </div>

        {/* Right — impact multi-select with live counts */}
        <div style={{display:'flex',gap:4,flexWrap:'wrap',justifyContent:'flex-end',flexShrink:0}}>
          {ALL_IMPACTS.map(imp => {
            const m  = IMPACT_META[imp];
            const on = impactFilter.has(imp);
            return (
              <ImpactBtn key={imp} impKey={imp}
                label={`${m.label} (${counts[imp]})`}
                dot={m.dot} on={on}
                bg={m.bg} color={m.color} border={m.border}
                onClick={()=>toggleImpact(imp)}/>
            );
          })}
          {/* Unclassified — grey CSS ball with count */}
          {(()=>{
            const on = impactFilter.has('');
            return (
              <button onClick={()=>toggleImpact('')} style={{
                display:'inline-flex', alignItems:'center', gap:3,
                fontSize:11, padding:'2px 8px', borderRadius:10, cursor:'pointer',
                fontWeight: on ? 700 : 400,
                border:`.5px solid ${on?'var(--amber-hair)':'var(--hair-strong)'}`,
                background: on ? 'var(--amber-bg)' : 'var(--surface)',
                color: on ? 'var(--amber-ink)' : 'var(--ink-3)',
                opacity: on ? 1 : 0.5,
                transition:'all .15s',
              }}>
                <Ball gradient={GREY_BALL} size={9}/> Unclassified ({counts['']})
              </button>
            );
          })()}
        </div>
      </div>

      {/* ── News items ── */}
      {items.length === 0 && (
        <div style={{fontSize:11,color:'var(--ink-3)',padding:'12px 0',textAlign:'center'}}>
          No items match the selected filters.
        </div>
      )}
      {items.map(n=>(
        <div key={n.id} style={{padding:'9px 0',borderBottom:'.5px solid var(--hair)',display:'flex',gap:9,alignItems:'flex-start'}}>
          <div style={{width:30,height:30,borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:13,background:ICON_BG[n.cat]||'var(--surface-2)'}}>{n.icon}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:3,flexWrap:'wrap'}}>
              <span className={`ncat-${n.catKey}`} style={{fontSize:11,padding:'2px 6px',borderRadius:3,fontWeight:600}}>{n.cat==='NSE/BSE Circulars'?'CIRCULAR':n.cat}</span>
              {n.impact
                ? <ImpactPill impact={n.impact}/>
                : <span style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:11,color:'var(--ink-3)'}}>
                    <Ball gradient={GREY_BALL} size={8}/> Unclassified
                  </span>
              }
              <span style={{fontSize:11,color:'var(--ink-3)'}}>{n.src} · {n.time} ago</span>
            </div>
            {n.url ? (
              <a href={n.url} target="_blank" rel="noopener noreferrer"
                style={{fontSize:12,color:'var(--ink)',lineHeight:1.42,textDecoration:'none',display:'block',cursor:'pointer'}}>
                {n.hl} <span style={{fontSize:11,color:'var(--amber-ink)',opacity:.7}}>↗</span>
              </a>
            ) : (
              <div style={{fontSize:12,color:'var(--ink)',lineHeight:1.42}}>{n.hl}</div>
            )}
            {n.rationale && (
              <div style={{fontSize:11,color:'var(--ink-3)',marginTop:3,fontStyle:'italic',lineHeight:1.4}}>
                ↳ {n.rationale}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

const SL=({children})=><div style={{fontSize:11,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',color:'var(--ink-3)',margin:'14px 0 7px'}}>{children}</div>;

/* ── Compact macro events strip for Dashboard ── */
const DASH_EVENT_META = {
  FOMC:                 {label:'FOMC',       color:'#f59e0b',bg:'rgba(245,158,11,.12)'},
  RBI_MPC:              {label:'RBI MPC',    color:'#6366f1',bg:'rgba(99,102,241,.12)'},
  US_CPI:               {label:'US CPI',     color:'#ef4444',bg:'rgba(239,68,68,.10)'},
  US_NFP:               {label:'US NFP',     color:'#f97316',bg:'rgba(249,115,22,.10)'},
  IN_BUDGET:            {label:'Budget',     color:'#ec4899',bg:'rgba(236,72,153,.10)'},
  IN_ELECTION:          {label:'Election',   color:'#8b5cf6',bg:'rgba(139,92,246,.10)'},
  NIFTY_MONTHLY_EXPIRY: {label:'Monthly Exp',color:'#64748b',bg:'rgba(100,116,139,.10)'},
  OTHER:                {label:'Event',      color:'#64748b',bg:'rgba(100,116,139,.10)'},
};

function DashboardEventsStrip() {
  const [events,  setEvents]  = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [loaded,  setLoaded]  = useState(false);

  useEffect(() => {
    getStrategy4Events(7).then(res => {
      setEvents(res.events || []);
      setMonthly(res.monthly_expiry_dates || []);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const all = [
    ...(events),
    ...(monthly.map(d => ({ event_date: d, event_type: 'NIFTY_MONTHLY_EXPIRY', title: 'NIFTY Monthly Expiry', impact: 'MEDIUM', source:'auto' }))),
  ].sort((a,b) => (a.event_date||'').localeCompare(b.event_date||''));

  if (!loaded || all.length === 0) return null;

  return (
    <div style={{
      marginBottom: 6,
      padding: '6px 10px',
      background: 'var(--surface)',
      border: '.5px solid var(--hair)',
      borderRadius: 6,
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    }}>
      <span style={{fontSize:11.5,fontWeight:700,color:'var(--ink-3)',textTransform:'uppercase',letterSpacing:'.05em',flexShrink:0}}>
        7-Day Events
      </span>
      {all.map((e, i) => {
        const meta = DASH_EVENT_META[e.event_type] || DASH_EVENT_META.OTHER;
        const daysAway = e.event_date
          ? Math.ceil((new Date(e.event_date) - new Date()) / 86400000)
          : null;
        const badge = daysAway === 0 ? 'Today' : daysAway === 1 ? 'Tomorrow' : daysAway != null ? `${daysAway}d` : '';
        return (
          <span key={i} style={{
            display:'inline-flex',alignItems:'center',gap:4,
            padding:'2px 6px',borderRadius:4,
            background:meta.bg,border:`.5px solid ${meta.color}40`,
            fontSize:11,whiteSpace:'nowrap',
          }}>
            <span style={{fontWeight:700,color:meta.color}}>{meta.label}</span>
            <span style={{color:'var(--ink-3)'}}>{e.event_date}</span>
            {badge && (
              <span style={{
                padding:'0 3px',borderRadius:2,fontSize:11,
                background:meta.color,color:'#fff',fontWeight:700,
              }}>{badge}</span>
            )}
            {e.impact === 'HIGH' && (
              <span style={{fontSize:11,color:meta.color,fontWeight:700}}>HIGH</span>
            )}
          </span>
        );
      })}
    </div>
  );
}

// ── Collapsible section wrapper (EIA / FII full panel) ───────────────────
// Persists open/collapsed in preferences table (server-side; global in H2, per-user in F3).
const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function useSectionCollapse(key, defaultCollapsed = true) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  // Load persisted state on mount
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/settings/preferences?key=${key}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.value != null) setCollapsed(d.value === 'true'); })
      .catch(() => {});
  }, [key]);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    // Persist asynchronously; ignore errors (best-effort)
    fetch(`${BACKEND_URL}/api/settings/preferences`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, value: String(next) }),
    }).catch(() => {});
  };

  return [collapsed, toggle];
}

function CollapsibleSection({ prefKey, title, defaultCollapsed = true, children }) {
  const [collapsed, toggle] = useSectionCollapse(prefKey, defaultCollapsed);
  return (
    <div style={{ marginBottom: 6 }}>
      <div
        onClick={toggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          cursor: 'pointer', userSelect: 'none',
          padding: '5px 0', marginBottom: collapsed ? 0 : 4,
        }}
      >
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
          color: 'var(--ink-3)',
        }}>
          {title}
        </span>
        <span style={{ fontSize: 12, color: 'var(--ink-3)', marginLeft: 2 }}>
          {collapsed ? '▸' : '▾'}
        </span>
      </div>
      {!collapsed && children}
    </div>
  );
}

// ── Heatmap lens caption ─────────────────────────────────────────────────
function HeatmapLens() {
  const [caption, setCaption] = useState('');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/today/heatmap-lens`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.caption) setCaption(d.caption); })
      .catch(() => {});
  }, []);

  if (!caption) return null;
  return (
    <div style={{
      fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic',
      padding: '4px 2px 8px', lineHeight: 1.5,
    }}>
      {caption}
    </div>
  );
}

// ── Date label helper (shared with TopBar) ───────────────────────────────
function fmtDateSection(isoDate) {
  if (!isoDate) return '';
  try {
    const today    = new Date(); today.setHours(0,0,0,0);
    const d        = new Date(isoDate + 'T00:00:00');
    const diffDays = Math.round((d - today) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return isoDate; }
}

const _SOURCE_STYLE = {
  MACRO:     { bg: 'var(--surface-2)',        color: 'var(--ink-2)'        },
  STRATEGY:  { bg: 'var(--up-field-bg)',      color: 'var(--up-field-ink)' },
  PORTFOLIO: { bg: 'var(--amber-bg)',         color: 'var(--amber-ink)'    },
  ACTIONS:   { bg: 'rgba(245,158,11,.10)',    color: 'var(--amber-ink)'    },
  SYSTEM:    { bg: 'var(--down-field-bg)',    color: 'var(--down-field-ink)' },
  CUSTOM:    { bg: 'var(--surface-2)',        color: 'var(--ink-3)'        },
};

// ── Today's Action List — inline Dashboard section (single list, date-grouped) ──
function TodayActionListInline() {
  const { allItems, refresh } = useToday();

  const handleToggleAction = async (itemId, isDone) => {
    const rawId = parseInt(itemId.replace('action-', ''), 10);
    if (isNaN(rawId)) return;
    try {
      if (isDone) {
        await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/strategy4/events/${rawId}/reopen`, { method: 'POST' });
      } else {
        await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/strategy4/events/${rawId}/complete`, { method: 'POST' });
      }
      await refresh();
    } catch (e) { /* silent */ }
  };

  // Group by due_date
  const groups = [];
  let lastDate = null;
  allItems.forEach(item => {
    if (item.due_date !== lastDate) {
      groups.push({ date: item.due_date, label: fmtDateSection(item.due_date), items: [] });
      lastDate = item.due_date;
    }
    groups[groups.length - 1].items.push(item);
  });

  return (
    <>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-3)', margin: '14px 0 7px' }}>
        Events &amp; Actions
      </div>
    <div className="card" style={{ padding: '9px 12px', marginBottom: 7 }}>
      {!allItems.length ? (
        <div style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>
          No action items in the next 7 days.
        </div>
      ) : (
        groups.map((group, gi) => (
          <div key={group.date}>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
              color: 'var(--ink-3)', padding: '6px 0 3px',
              borderTop: gi === 0 ? 'none' : '.5px solid var(--hair)',
              marginTop: gi === 0 ? 0 : 4,
            }}>
              {group.label}
            </div>
            {group.items.map(item => {
              const done      = !!item.completed_at;
              const isAction  = item.source === 'ACTIONS';
              const ss        = _SOURCE_STYLE[item.source] || _SOURCE_STYLE.CUSTOM;
              return (
                <div key={item.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  padding: '5px 0',
                  opacity: done ? 0.6 : 1,
                }}>
                  {/* Checkbox only for user actions, not for market events */}
                  {isAction ? (
                    <input
                      type="checkbox"
                      checked={done}
                      onChange={() => handleToggleAction(item.id, done)}
                      style={{ marginTop: 3, flexShrink: 0, cursor: 'pointer', accentColor: 'var(--amber-ink)' }}
                    />
                  ) : (
                    <span style={{ width: 13, flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                      <span style={{
                        background: ss.bg, color: ss.color,
                        fontSize: 9, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                        padding: '1px 5px', borderRadius: 'var(--r-1)', whiteSpace: 'nowrap',
                      }}>{item.source}</span>
                      <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>{item.time}</span>
                      {item.urgent && !done && <span style={{ color: 'var(--amber-ink)', fontSize: 11 }}>⚡</span>}
                      {item.overdue && !done && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--amber-ink)',
                          background: 'rgba(245,158,11,.12)', padding: '1px 4px', borderRadius: 2 }}>
                          OVERDUE
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-1)', fontWeight: 500,
                      textDecoration: done && isAction ? 'line-through' : 'none' }}>
                      {item.title}
                    </div>
                    {item.description && (isAction || item.source === 'SYSTEM') && (
                      <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 1 }}>{item.description}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
    </>
  );
}

// ── Morning Digest H2 ────────────────────────────────────────────────────────
const BIAS_STYLE = {
  OPTIMISTIC: { color: 'var(--up-field-ink)',   rail: '#22a35a' },
  BEARISH:    { color: 'var(--down-field-ink)', rail: '#e03030' },
  CAUTIOUS:   { color: 'var(--amber-ink)',      rail: '#c8922a' },
  VOLATILE:   { color: 'var(--amber-ink)',      rail: '#c8922a' },
  NEUTRAL:    { color: 'var(--ink-2)',          rail: '#999'    },
};

// Evidence stat chip — label above, value below
function EvidenceStat({ label, value, valueColor }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
        {label}
      </span>
      <span style={{ fontSize: 11, fontWeight: 600, color: valueColor || 'var(--ink)', letterSpacing: '.01em' }}>
        {value ?? '—'}
      </span>
    </div>
  );
}

// ── Pure render — accepts a digest report_json object, no data fetching ──────
// Used by AISummaryTab to render historical day cards with the same layout.
export function MorningDigestCardBody({ digest }) {
  if (!digest) return null;

  // Support both shaped (endpoint) and raw (report_json) field names
  const _BIAS_MAP = { bullish:'OPTIMISTIC', optimistic:'OPTIMISTIC', bearish:'BEARISH', cautious:'CAUTIOUS', volatile:'VOLATILE', neutral:'NEUTRAL' };
  const rawBias    = digest.bias || 'NEUTRAL';
  const bias       = _BIAS_MAP[rawBias.toLowerCase()] || rawBias.toUpperCase();
  const bs         = BIAS_STYLE[bias] || BIAS_STYLE.NEUTRAL;
  // rationale: endpoint sets it; raw json stores in domestic/headline
  const rationale  = digest.rationale || digest.domestic || digest.headline || '';
  // overnight: endpoint → overnight_summary.bullets; raw json → overnight string or array
  const _ov = digest.overnight_summary?.bullets
    || (typeof digest.overnight === 'string' ? [digest.overnight] : null)
    || (Array.isArray(digest.overnight) ? digest.overnight.map(o => o.text || o).filter(Boolean) : []);
  const overnight  = _ov;
  const stories    = digest.top_stories || [];
  // Dashboard endpoint remaps _input_snapshot → input_snapshot; raw report_json keeps the underscore prefix
  const isnap      = digest.input_snapshot  || digest._input_snapshot  || null;
  const oldSnap    = digest.market_snapshot || digest._market_snapshot || {};

  const pctColor = v => v == null ? 'var(--ink)' : v >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)';
  const fmtNum   = (v, dec = 0) => v == null ? null : v.toLocaleString('en-IN', { minimumFractionDigits:dec, maximumFractionDigits:dec });

  const eod       = isnap?.eod || {};
  const giftField = isnap?.gift_signal || {};
  const snapClose  = isnap ? eod.close?.value     : oldSnap.close;
  const snapChgPct = isnap ? eod.chg_pct?.value   : oldSnap.chg_pct;
  const snapVix    = isnap ? eod.vix?.value       : oldSnap.vix;
  const snapVixChg = isnap ? eod.vix_chg?.value   : oldSnap.vix_chg;
  const snapAdv    = isnap ? eod.adv?.value       : oldSnap.adv;
  const snapDec    = isnap ? eod.dec_count?.value : oldSnap.dec_count;
  const snapFii    = isnap ? eod.fii?.value       : oldSnap.fii;
  const snapDii    = isnap ? eod.dii?.value       : oldSnap.dii;
  const snapGiftGap = isnap ? giftField.gap_pts   : oldSnap.gift_gap_pts;
  const giftStatus  = isnap ? (giftField.status || 'unavailable') : (oldSnap.gift_gap_pts != null ? 'available' : 'unavailable');

  const eodDate   = isnap?.eod_date;
  const genAt     = isnap?.generated_at;
  const fmtShortDate = d => { try { return new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',timeZone:'Asia/Kolkata'}); } catch{return d;} };
  const fmtTime = iso => { try { return new Date(iso.includes('T')?iso:iso.replace(' ','T')).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:false,timeZone:'Asia/Kolkata'})+' IST'; } catch{return null;} };
  const eodLabel  = eodDate  ? fmtShortDate(eodDate) : null;
  const liveLabel = genAt    ? fmtTime(genAt)         : null;

  return (
    <div className="card" style={{ padding:0, marginBottom:7, borderLeft:`3px solid ${bs.rail}`, overflow:'hidden' }}>
      <div className="md-inner" style={{ display:'flex', alignItems:'stretch' }}>

        {/* Left: bias hero + rationale + evidence */}
        <div className="md-left-panel" style={{ width:240, flexShrink:0, display:'flex', flexDirection:'column',
          padding:'14px 16px 12px', borderRight:'.5px solid var(--hair)' }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase',
            color:'var(--ink-3)', marginBottom:6 }}>Today's Call</div>
          <div style={{ fontSize:30, fontWeight:800, color:bs.color,
            letterSpacing:'-.01em', lineHeight:1, marginBottom:10 }}>
            {bias.charAt(0) + bias.slice(1).toLowerCase()}
          </div>
          {rationale
            ? <p style={{ fontSize:11, color:'var(--ink)', lineHeight:1.55, margin:'0 0 12px', flex:1 }}>{rationale}</p>
            : <p style={{ fontSize:11, color:'var(--ink-3)', fontStyle:'italic', margin:'0 0 12px', flex:1 }}/>}
          <div style={{ borderTop:'.5px solid var(--hair)', paddingTop:10,
            display:'flex', flexWrap:'wrap', gap:'8px 14px' }}>
            <EvidenceStat label="NIFTY"
              value={snapClose != null ? `${fmtNum(snapClose,2)}${snapChgPct!=null?` (${snapChgPct>=0?'+':''}${snapChgPct.toFixed(2)}%)`:''}` : '—'}
              valueColor={pctColor(snapChgPct)}/>
            <EvidenceStat label="Breadth"
              value={(snapAdv!=null&&snapDec!=null) ? `${snapAdv}▲ / ${snapDec}▼` : '—'}
              valueColor={(snapAdv!=null&&snapDec!=null) ? (snapAdv>snapDec?'var(--up-field-ink)':snapAdv<snapDec?'var(--down-field-ink)':'var(--ink)') : 'var(--ink-3)'}/>
            <EvidenceStat label="India VIX"
              value={snapVix!=null ? `${fmtNum(snapVix,2)}${snapVixChg!=null?` (${snapVixChg>=0?'+':''}${snapVixChg.toFixed(2)})`:''}` : '—'}
              valueColor={snapVixChg!=null?(snapVixChg>=0?'var(--down-field-ink)':'var(--up-field-ink)'):'var(--ink)'}/>
            <div style={{ display:'flex', flexDirection:'column', gap:1 }}>
              <span style={{ fontSize:9, fontWeight:600, letterSpacing:'.07em', textTransform:'uppercase', color:'var(--ink-3)' }}>FII · DII</span>
              <span style={{ fontSize:11, fontWeight:600 }}>
                {snapFii!=null
                  ? <span style={{ color:snapFii>=0?'var(--up-field-ink)':'var(--down-field-ink)' }}>{snapFii>=0?'+':''}{Math.round(snapFii).toLocaleString('en-IN')}</span>
                  : <span style={{ color:'var(--ink-3)',fontStyle:'italic' }}>—</span>}
                <span style={{ color:'var(--ink-3)',margin:'0 3px' }}>·</span>
                {snapDii!=null
                  ? <span style={{ color:snapDii>=0?'var(--up-field-ink)':'var(--down-field-ink)' }}>{snapDii>=0?'+':''}{Math.round(snapDii).toLocaleString('en-IN')}</span>
                  : <span style={{ color:'var(--ink-3)',fontStyle:'italic' }}>—</span>}
              </span>
            </div>
            <EvidenceStat label="GIFT Nifty"
              value={(giftStatus==='unavailable'||snapGiftGap==null) ? '—' : `${snapGiftGap>=0?'+':''}${Math.round(snapGiftGap)} pts`}
              valueColor={giftStatus==='unavailable'?'var(--ink-3)':pctColor(snapGiftGap)}/>
          </div>
          <div style={{ marginTop:6, display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:2 }}>
            {eodLabel  && <span style={{ fontSize:9, color:'var(--ink-3)' }}>EOD {eodLabel}</span>}
            {liveLabel && <span style={{ fontSize:9, color:'var(--ink-3)' }}>GIFT/Global {liveLabel}</span>}
          </div>
        </div>

        {/* Right: stories + overnight */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', padding:'14px 14px 12px', minWidth:0 }}>
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase',
              color:'var(--ink-3)', marginBottom:8, display:'flex', alignItems:'center', gap:5 }}>
              Top Stories
              {stories.length > 0 && <span style={{ fontWeight:400 }}>({stories.length})</span>}
            </div>
            {stories.length > 0
              ? stories.map((s, i) => (
                  <div key={i} style={{ paddingBottom:8, marginBottom:8,
                    borderBottom: i < stories.length-1 ? '.5px solid var(--hair)' : 'none' }}>
                    <div style={{ fontSize:11.5, fontWeight:600, color:'var(--ink)', lineHeight:1.4, marginBottom:2 }}>
                      {s.url
                        ? <a href={s.url} target="_blank" rel="noreferrer" style={{ color:'inherit', textDecoration:'none' }}>{s.headline}</a>
                        : s.headline}
                    </div>
                    {s.why && <div style={{ fontSize:10, color:'var(--ink-3)', lineHeight:1.4 }}>{s.why}</div>}
                  </div>
                ))
              : <div style={{ fontSize:11, color:'var(--ink-3)', fontStyle:'italic' }}>No stories yet.</div>}
          </div>
          <div style={{ borderTop:'.5px solid var(--hair)', paddingTop:10 }}>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase',
              color:'var(--ink-3)', marginBottom:6 }}>Overnight</div>
            {overnight.length > 0
              ? overnight.map((b,i) => <p key={i} style={{ fontSize:11, color:'var(--ink)', lineHeight:1.6, margin:0 }}>{b}</p>)
              : <p style={{ fontSize:11, color:'var(--ink-3)', fontStyle:'italic', margin:0 }}>No overnight data.</p>}
            {digest.overnight_news_bullets?.length > 0 && (
              <div style={{ marginTop:8 }}>
                <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase',
                  color:'var(--ink-3)', marginBottom:4 }}>Overnight News</div>
                {digest.overnight_news_bullets.map((b,i) => (
                  <p key={i} style={{ fontSize:11, color:'var(--ink)', lineHeight:1.6, margin:'0 0 2px 0', display:'flex', gap:5 }}>
                    <span style={{ color:'var(--ink-3)', flexShrink:0 }}>•</span>{b}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function MorningDigestH2() {
  const [digest,     setDigest]     = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genMsg,     setGenMsg]     = useState('');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/today/morning-digest`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setDigest(d); })
      .catch(() => {});
  }, []);

  const handleRegenerate = async () => {
    setGenerating(true);
    setGenMsg('Generating… (20–60s)');
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/generate/dashboard`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      let attempts = 0;
      const prevDate    = digest?.report_date || '';
      const prevVersion = digest?.version ?? -1;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const r = await fetch(`${BACKEND_URL}/api/today/morning-digest`);
          const d = r.ok ? await r.json() : null;
          if (d && (d.report_date !== prevDate || (d.version != null && d.version !== prevVersion))) {
            clearInterval(poll); setDigest(d); setGenerating(false);
            setGenMsg('✓ Generated!'); setTimeout(() => setGenMsg(''), 4000);
          }
        } catch {}
        if (attempts >= 30) { clearInterval(poll); setGenerating(false); setGenMsg('⚠ Timed out — refresh in a minute'); }
      }, 6000);
    } catch (e) { setGenerating(false); setGenMsg(`Error: ${e.message}`); }
  };

  const bias       = digest?.bias || 'NEUTRAL';
  const bs         = BIAS_STYLE[bias] || BIAS_STYLE.NEUTRAL;
  const rationale  = digest?.rationale || '';
  const overnight  = digest?.overnight_summary?.bullets || [];
  const stories    = digest?.top_stories || [];
  // Generation stamp: "18 Jun · 06:30 · Opus"
  const genStamp = digest?.generated_at
    ? (() => {
        const d = new Date(digest.generated_at.replace(' ', 'T') + 'Z');
        const datePart = d.toLocaleString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' });
        const timePart = d.toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
        const model    = digest.model_short;
        return model ? `${datePart} · ${timePart} · ${model}` : `${datePart} · ${timePart}`;
      })()
    : null;

  // ── Evidence values — ONLY from the frozen input_snapshot stored at generation time.
  // NO live fallback. null frozen field → "—" + absent label, never a live substitute.
  const isnap   = digest?.input_snapshot;   // structured (new reports)
  const oldSnap = digest?.market_snapshot || {};  // flat (old reports, backward-compat only)

  const pctColor = (v) => v == null ? 'var(--ink)' : v >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)';
  const fmtNum   = (v, dec = 0) => v == null ? null : v.toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });

  const fmtShortDate = d => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' }); }
    catch { return d; }
  };
  const fmtTime = iso => {
    if (!iso) return null;
    try {
      const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T'));
      return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' }) + ' IST';
    } catch { return null; }
  };

  // New structured format
  const eod         = isnap?.eod || {};
  const giftField   = isnap?.gift_signal || {};
  const eodDate     = isnap?.eod_date;
  const genAt       = isnap?.generated_at;

  // Frozen scalars — value is null if unavailable (no live substitute)
  const snapClose   = isnap ? eod.close?.value     : oldSnap.close;
  const snapChgPct  = isnap ? eod.chg_pct?.value   : oldSnap.chg_pct;
  const snapVix     = isnap ? eod.vix?.value       : oldSnap.vix;
  const snapVixChg  = isnap ? eod.vix_chg?.value   : oldSnap.vix_chg;
  const snapAdv     = isnap ? eod.adv?.value       : oldSnap.adv;
  const snapDec     = isnap ? eod.dec_count?.value : oldSnap.dec_count;
  const snapFii     = isnap ? eod.fii?.value       : oldSnap.fii;
  const snapDii     = isnap ? eod.dii?.value       : oldSnap.dii;
  const snapGiftGap = isnap ? giftField.gap_pts    : oldSnap.gift_gap_pts;
  const giftStatus  = isnap ? (giftField.status || 'unavailable') : (oldSnap.gift_gap_pts != null ? 'available' : 'unavailable');

  // Two-tier freshness labels
  const eodLabel  = fmtShortDate(eodDate);
  const liveLabel = fmtTime(genAt);
  // Single "Data as of" fallback for old flat reports
  const snapLabel = !isnap && digest?.generated_at
    ? (() => {
        const d = new Date(digest.generated_at.replace(' ', 'T') + 'Z');
        const day  = d.toLocaleString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' });
        const time = d.toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
        return `Data as of ${day} ${time} (old format — re-generate for full detail)`;
      })()
    : null;

  const hasData = !!(rationale || stories.length || overnight.length);

  return (
    <>
      {/* Section heading row — no background, all on paper */}
      <div style={{ display: 'flex', alignItems: 'center', margin: '14px 0 7px' }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
          Morning Digest
        </span>
        <div style={{ flex: 1 }} />
        {genMsg ? (
          <span style={{ fontSize: 11, fontWeight: 600, marginRight: 8,
            color: genMsg.startsWith('✓') ? 'var(--up-field-ink)' : genMsg.startsWith('⚠') ? 'var(--amber-ink)' : 'var(--ink-2)' }}>
            {genMsg}
          </span>
        ) : genStamp ? (
          <span style={{ fontSize: 10, color: 'var(--ink-3)', marginRight: 8 }}>{genStamp}</span>
        ) : null}
        <button
          onClick={handleRegenerate} disabled={generating}
          style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 4,
            border: '.5px solid var(--hair-strong)', background: 'var(--surface-2)', color: 'var(--ink-2)',
            cursor: generating ? 'default' : 'pointer', opacity: generating ? 0.6 : 1 }}>
          {generating ? '⏳' : '⚡ Generate'}
        </button>
      </div>

      {/* Main card — amber left rail */}
      <div className="card" style={{ padding: 0, marginBottom: 7, borderLeft: `3px solid ${bs.rail}`, overflow: 'hidden' }}>
        <div className="md-inner" style={{ display: 'flex', alignItems: 'stretch' }}>

          {/* ── Left panel: bias hero + rationale + evidence ── */}
          <div className="md-left-panel" style={{ width: 240, flexShrink: 0, display: 'flex', flexDirection: 'column',
            padding: '14px 16px 12px', borderRight: '.5px solid var(--hair)' }}>

            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
              color: 'var(--ink-3)', marginBottom: 6 }}>
              Today's Call
            </div>

            {/* Hero bias word */}
            <div style={{ fontSize: 30, fontWeight: 800, color: bs.color,
              letterSpacing: '-.01em', lineHeight: 1, marginBottom: 10 }}>
              {bias.charAt(0) + bias.slice(1).toLowerCase()}
            </div>

            {/* Rationale prose */}
            {rationale ? (
              <p style={{ fontSize: 11, color: 'var(--ink)', lineHeight: 1.55, margin: '0 0 12px', flex: 1 }}>
                {rationale}
              </p>
            ) : (
              <p style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic', margin: '0 0 12px', flex: 1 }}>
                {hasData ? '' : "No digest yet — hit ⚡ Generate to run today’s report."}
              </p>
            )}

            {/* Evidence row — reads ONLY from frozen snapshot, no live substitution */}
            <div style={{ borderTop: '.5px solid var(--hair)', paddingTop: 10,
              display: 'flex', flexWrap: 'wrap', gap: '8px 14px' }}>

              {/* NIFTY — show "—" if unavailable in snapshot, never live price */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>NIFTY</span>
                {snapClose != null
                  ? <span style={{ fontSize: 11, fontWeight: 600, color: pctColor(snapChgPct) }}>
                      {fmtNum(snapClose, 2)}{snapChgPct != null ? ` (${snapChgPct >= 0 ? '+' : ''}${snapChgPct.toFixed(2)}%)` : ''}
                    </span>
                  : <span style={{ fontSize: 10, color: 'var(--ink-3)', fontStyle: 'italic' }}>—<span style={{ fontSize: 8, marginLeft: 2 }}>n/a</span></span>
                }
              </div>

              {/* Breadth — show "—" if either absent, never live breadth */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Breadth</span>
                {(snapAdv != null && snapDec != null)
                  ? <span style={{ fontSize: 11, fontWeight: 600, color: snapAdv > snapDec ? 'var(--up-field-ink)' : snapAdv < snapDec ? 'var(--down-field-ink)' : 'var(--ink)' }}>
                      {snapAdv}▲ / {snapDec}▼
                    </span>
                  : <span style={{ fontSize: 10, color: 'var(--ink-3)', fontStyle: 'italic' }}>—<span style={{ fontSize: 8, marginLeft: 2 }}>n/a</span></span>
                }
              </div>

              {/* VIX — show "—" if unavailable */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>India VIX</span>
                {snapVix != null
                  ? <span style={{ fontSize: 11, fontWeight: 600, color: snapVixChg != null ? (snapVixChg >= 0 ? 'var(--down-field-ink)' : 'var(--up-field-ink)') : 'var(--ink)' }}>
                      {fmtNum(snapVix, 2)}{snapVixChg != null ? ` (${snapVixChg >= 0 ? '+' : ''}${snapVixChg.toFixed(2)})` : ''}
                    </span>
                  : <span style={{ fontSize: 10, color: 'var(--ink-3)', fontStyle: 'italic' }}>—<span style={{ fontSize: 8, marginLeft: 2 }}>n/a</span></span>
                }
              </div>

              {/* FII · DII — from frozen eod fields, never re-queried live */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>FII · DII</span>
                <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.01em' }}>
                  {snapFii != null
                    ? <span style={{ color: snapFii >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
                        {snapFii >= 0 ? '+' : ''}{Math.round(snapFii).toLocaleString('en-IN')}
                      </span>
                    : <span style={{ color: 'var(--ink-3)', fontStyle: 'italic' }}>—</span>
                  }
                  <span style={{ color: 'var(--ink-3)', margin: '0 3px' }}>·</span>
                  {snapDii != null
                    ? <span style={{ color: snapDii >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
                        {snapDii >= 0 ? '+' : ''}{Math.round(snapDii).toLocaleString('en-IN')}
                      </span>
                    : <span style={{ color: 'var(--ink-3)', fontStyle: 'italic' }}>—</span>
                  }
                </span>
              </div>

              {/* GIFT — always rendered; "unavailable at generation" shown explicitly */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>GIFT Nifty</span>
                {giftStatus === 'unavailable' || snapGiftGap == null
                  ? <span style={{ fontSize: 10, color: 'var(--down-field-ink)', fontStyle: 'italic' }}>
                      —<span style={{ fontSize: 8, marginLeft: 2 }}>n/a</span>
                    </span>
                  : <span style={{ fontSize: 11, fontWeight: 600, color: pctColor(snapGiftGap) }}>
                      {snapGiftGap >= 0 ? '+' : ''}{Math.round(snapGiftGap)} pts
                      {giftStatus === 'stale' && <span style={{ fontSize: 8, color: 'var(--amber-ink)', marginLeft: 3 }}>stale</span>}
                    </span>
                }
              </div>
            </div>

            {/* Two-tier freshness stamps — EOD date + live-fetch time, never single mixed stamp */}
            <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              {eodLabel && <span style={{ fontSize: 9, color: 'var(--ink-3)' }}>EOD {eodLabel}</span>}
              {liveLabel && <span style={{ fontSize: 9, color: 'var(--ink-3)' }}>GIFT/Global {liveLabel}</span>}
              {snapLabel && <span style={{ fontSize: 9, color: 'var(--ink-3)' }}>{snapLabel}</span>}
            </div>
          </div>

          {/* ── Right panel: stories + overnight ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '14px 14px 12px', minWidth: 0 }}>

            {/* Top Stories */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
                color: 'var(--ink-3)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                Top Stories
                {stories.length > 0 && <span style={{ fontWeight: 400 }}>({stories.length})</span>}
              </div>
              {stories.length > 0
                ? stories.map((s, i) => (
                    <div key={i} style={{ paddingBottom: 8, marginBottom: 8,
                      borderBottom: i < stories.length - 1 ? '.5px solid var(--hair)' : 'none' }}>
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.4, marginBottom: 2 }}>
                        {s.url
                          ? <a href={s.url} target="_blank" rel="noreferrer"
                              style={{ color: 'inherit', textDecoration: 'none' }}>{s.headline}</a>
                          : s.headline}
                      </div>
                      {s.why && (
                        <div style={{ fontSize: 10, color: 'var(--ink-3)', lineHeight: 1.4 }}>{s.why}</div>
                      )}
                    </div>
                  ))
                : <div style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                    No stories yet.
                  </div>
              }
            </div>

            {/* Overnight */}
            <div style={{ borderTop: '.5px solid var(--hair)', paddingTop: 10 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
                color: 'var(--ink-3)', marginBottom: 6 }}>
                Overnight
              </div>
              {overnight.length > 0
                ? overnight.map((b, i) => (
                    <p key={i} style={{ fontSize: 11, color: 'var(--ink)', lineHeight: 1.6, margin: 0 }}>{b}</p>
                  ))
                : <p style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic', margin: 0 }}>
                    No overnight data yet.
                  </p>
              }
              {(digest?.overnight_news_bullets?.length > 0) && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
                    color: 'var(--ink-3)', marginBottom: 4 }}>
                    Overnight News
                  </div>
                  {digest.overnight_news_bullets.map((b, i) => (
                    <p key={i} style={{ fontSize: 11, color: 'var(--ink)', lineHeight: 1.6, margin: '0 0 2px 0', display: 'flex', gap: 5 }}>
                      <span style={{ color: 'var(--ink-3)', flexShrink: 0 }}>•</span>{b}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function DashboardTab() {
  const d = useAdaptedData();

  // Sync module-level vars so all original components read live data
  INDICES=d.INDICES; GLOBAL_MARKETS=d.GLOBAL_MARKETS; SGX=d.SGX; COMMODITIES=d.COMMODITIES; EIA=d.EIA;
  BREADTH=d.BREADTH; SECTORS=d.SECTORS; BROAD=d.BROAD; THEMATICS=d.THEMATICS; FO_MOVERS=d.FO_MOVERS;
  EXTREMES=d.EXTREMES;
  FII_HISTORY=d.fiiHistory||[];
  NEWS=_NEWS_DATA;

  return (
    <div style={{maxWidth:1400,margin:'0 auto'}}>

      {/* ── Market status legend — top right ── */}
      <div style={{display:'flex',justifyContent:'flex-end',gap:12,alignItems:'center',marginBottom:4}}>
        {[['#4a90d9','Live data'],['#c8922a','Waiting'],['#999','Closed']].map(([bg,label])=>(
          <span key={label} style={{fontSize:11,color:'var(--ink-3)',display:'flex',alignItems:'center',gap:4}}>
            <span style={{width:7,height:7,borderRadius:'50%',background:bg,display:'inline-block'}}/>
            {label}
          </span>
        ))}
      </div>

      {/* ── Row 1: Indian Markets (2×2) + Global Markets (2×3) ── */}
      <div className="dash-row1" style={{display:'grid',gridTemplateColumns:'2fr 3fr',gap:10,marginBottom:6}}>
        <InstrumentGroup
          title="Indian Markets"
          ids={['NIFTY50','BANKNIFTY','VIX','SENSEX']}
          items={INDICES}
          cols={2}
        />
        <InstrumentGroup
          title="Global Markets"
          ids={['nikkei','hangseng','dax','dow','sp500','nasdaq']}
          items={GLOBAL_MARKETS}
          cols={3}
        />
      </div>

      {/* ── Row 2: Oil & Gas (2×1) · Metals (2×1) · Currency & Crypto (2×1) ── */}
      <div className="dash-row2" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:6}}>
        <InstrumentGroup title="Oil &amp; Gas"         ids={['ng_us','crude_us']}   items={COMMODITIES} cols={2}/>
        <InstrumentGroup title="Metals"                ids={['gold_us','silver_us']} items={COMMODITIES} cols={2}/>
        <InstrumentGroup title="Currency &amp; Crypto" ids={['usdinr','btc']}        items={COMMODITIES} cols={2}/>
      </div>
      {/* ── FII Futures + GIFT Nifty pre-market in one row ── */}
      <div className="dash-fii-row" style={{display:'flex',alignItems:'stretch',gap:10,marginBottom:6,marginTop:2}}>
        <FIIFuturesRow/>
        <SGXCard/>
      </div>

      {/* 7-Day Events strip removed — superseded by Today's Action List (H2) */}

      {/* ── Morning Digest (H2 restructured) ── */}
      <MorningDigestH2/>

      {/* ── Sector Heatmap + AI lens caption ── */}
      <SL>Sector Heatmap<NseDot/></SL>
      <HeatGrid title="Sector &amp; Thematic Indices" items={[...SECTORS, ...THEMATICS]}/>
      <HeatGrid title="Broad Market Indices" items={BROAD}/>
      <HeatmapLens/>

      {/* ── Broad Market / Breadth ── */}
      <SL>Market Breadth</SL>
      <div className="card" style={{padding:'10px 12px',marginBottom:6}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{flex:1}}>
            <BreadthRow label="NIFTY 50" data={BREADTH.nifty50}/>
            <BreadthRow label="F&amp;O Universe" data={BREADTH.fo}/>
          </div>
          <BreadthHistChart hist={BREADTH.nifty50.hist}/>
        </div>
      </div>

      <SL>F&amp;O Movers<NseDot/></SL>
      <Movers/>

      <SL>52-Week Extremes Today<NseDot/></SL>
      <Extremes/>

      {/* ── EIA — collapsed by default, persisted ── */}
      <CollapsibleSection prefKey="dashboard-eia-collapsed" title="EIA Natural Gas Report" defaultCollapsed={true}>
        <EIAWidget/>
      </CollapsibleSection>

      {/* ── FII full panel — collapsed by default, persisted ── */}
      {/* News Feed removed from Dashboard — now lives in News & Digest tab (R3) */}
    </div>
  );
}
